import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, ChevronRight, ChevronLeft, Sparkles, HelpCircle } from "lucide-react";

/**
 * SALESFORGE AUDIT MASTER WIZARD
 * -------------------------------------------------------
 * Ziel:
 * - Premium Audit Wizard mit hoher diagnostischer Tiefe
 * - Sales-Kennzahlen bleiben Kern
 * - 30+ Diagnosefelder mit abhängigen Fragen
 * - starke Personalisierung für Ergebnis + Handlungsempfehlung
 * - normalisierte Downstream-Logik
 */

// -------------------------------------------------------
// TYPES
// -------------------------------------------------------

type GoalOption =
  | "more-qualified-leads"
  | "higher-conversion"
  | "shorter-sales-cycle"
  | "better-forecast"
  | "revenue-growth"
  | "more-pipeline"
  | "not-known";

type PainOption =
  | "too-few-leads"
  | "poor-lead-quality"
  | "too-few-discovery-calls"
  | "low-conversion"
  | "slow-response"
  | "weak-follow-up"
  | "missing-pipeline-structure"
  | "unreliable-forecast"
  | "long-sales-cycle"
  | "low-win-rate"
  | "not-known";

type ResponseTimeOption =
  | "under-1h"
  | "1-3h"
  | "same-day"
  | "1-3days"
  | "more-than-3days"
  | "not-known";

type FollowUpDisciplineOption =
  | "very-structured"
  | "partly-structured"
  | "ad-hoc"
  | "hardly-any"
  | "not-known";

type FollowUpFrequencyOption =
  | "same-day"
  | "within-2days"
  | "within-week"
  | "irregular"
  | "not-known";

type LeadQualityOption =
  | "very-high"
  | "good"
  | "mixed"
  | "weak"
  | "not-known";

type PipelineTransparencyOption =
  | "full"
  | "partial"
  | "low"
  | "minimal"
  | "not-known";

type YesNoUnknown = "yes" | "no" | "partly" | "not-known";
type IcpClarityOption = "very-clear" | "mostly-clear" | "vague" | "not-defined" | "not-known";
type MarketPresenceOption = "strong" | "moderate" | "weak" | "unknown" | "not-known";

type CompanySizeOption =
  | "1-10"
  | "11-50"
  | "51-200"
  | "201-1000"
  | "1000+"
  | "not-known";

type BusinessModelOption =
  | "b2b-services"
  | "b2b-saas"
  | "b2b-industrial"
  | "agency"
  | "consulting"
  | "ecommerce"
  | "other"
  | "not-known";

type PipelineSourceOption =
  | "outbound"
  | "inbound"
  | "referrals"
  | "existing-customers"
  | "partner-channel"
  | "mixed"
  | "not-known";

type BiggestDropoffOption =
  | "lead-to-discovery"
  | "discovery-to-offer"
  | "offer-to-close"
  | "unclear"
  | "not-known";

type TouchpointsToCloseOption =
  | "1-2"
  | "3-5"
  | "6-10"
  | "10+"
  | "not-known";

type ForecastAccuracyOption =
  | "very-accurate"
  | "within-20-percent"
  | "unreliable"
  | "not-known";

type ReviewCadenceOption =
  | "weekly"
  | "biweekly"
  | "monthly"
  | "rarely"
  | "never"
  | "not-known";

type CRMUsageOption =
  | "fully-used"
  | "partly-used"
  | "barely-used"
  | "not-known";

type OwnershipClarityOption =
  | "clear"
  | "partly-clear"
  | "unclear"
  | "not-known";

type LostLeadsOption =
  | "rarely"
  | "regularly"
  | "often"
  | "not-known";

type ConversionRangeOption =
  | "under-10"
  | "10-20"
  | "20-30"
  | "30-50"
  | "50+"
  | "not-known";

// NEW: Sales Rep Performance Types (v2)
type RevenueVisibilityOption =
  | "exact"
  | "estimated"
  | "not-known";

type RepsOnTrackOption =
  | "almost-all"
  | "more-than-half"
  | "less-than-half"
  | "hardly-any"
  | "not-known";

type PerformanceVarianceOption =
  | "low"
  | "moderate"
  | "high"
  | "very-high"
  | "not-known";

type RepsWithEnoughPipelineOption =
  | "almost-all"
  | "more-than-half"
  | "less-than-half"
  | "hardly-any"
  | "not-known";

type PipelineConfidenceOption =
  | "very-high"
  | "rather-high"
  | "rather-low"
  | "very-low"
  | "not-known";

// DealMomentumOption removed — field replaced by stalePipelineSharePercent

type RepReviewCadenceOption =
  | "weekly"
  | "biweekly"
  | "monthly"
  | "rarely"
  | "never"
  | "not-known";

// NEW: Additional types from patch spec
type DecisionMakerInvolvementOption =
  | "first-meeting"
  | "mid-process"
  | "late-process"
  | "never"
  | "not-known";

type LeadScoringOption =
  | "formal"
  | "informal"
  | "none"
  | "not-known";

type ProposalSpeedOption =
  | "same-day"
  | "1-3days"
  | "4-7days"
  | "over-week"
  | "not-known";

type RevenueTargetBreakdownOption =
  | "yes"
  | "partial"
  | "no"
  | "not-known";

// Legacy types (kept for compatibility)
type RevenueTargetCoverageOption =
  | "under-1x"
  | "1x-2x"
  | "2x-3x"
  | "3x+"
  | "not-known";

type PipelineQualificationOption =
  | "clear"
  | "partly"
  | "none"
  | "not-known";

type StalePipelineOption =
  | "under-10"
  | "10-25"
  | "25-50"
  | "over-50"
  | "not-known";

type PlaybookOption =
  | "comprehensive"
  | "partial"
  | "none"
  | "not-known";

type RampUpOption =
  | "under-1"
  | "1-3"
  | "3-6"
  | "6+"
  | "not-known";

export type AuditFormData = {
  // Step 1: Sales-Kennzahlen
  averageDealSize: number | null;
  averageDealSizeRange: string | null;
  salesCycleMonths: number | null;
  salesCycleRange: string | null;
  monthlyRevenueOverride: number | null;
  monthlyDeals: number | null;
  monthlyContacts: number | null;
  monthlyDiscoveryCalls: number | null;
  monthlyPresentations: number | null;

  // Step 2: Conversion & Funnel
  conversionLeadToDiscovery: number | null;
  conversionLeadToDiscoveryRange: ConversionRangeOption;
  conversionDiscoveryToOffer: number | null;
  conversionDiscoveryToOfferRange: ConversionRangeOption;
  conversionOfferToClose: number | null;
  conversionOfferToCloseRange: ConversionRangeOption;
  biggestDropoff: BiggestDropoffOption;
  touchpointsToClose: TouchpointsToCloseOption;
  stageExitCriteria: YesNoUnknown;

  // Step 2 new fields
  daysFromDiscoveryToProposal: number | null;
  proposalsWithNextStepPercent: number | null;

  // Step 3: Speed & Execution
  responseTime: ResponseTimeOption;
  timeToFirstMeetingDays: number | null;
  followUpDiscipline: FollowUpDisciplineOption;
  followUpFrequency: FollowUpFrequencyOption;
  followUpOwnership: OwnershipClarityOption;
  lostLeadsNoResponse: LostLeadsOption;
  // Step 3 new fields
  avgFollowUpsPerLead: number | null;
  decisionMakerInFirstMeetingPercent: number | null;
  decisionMakerInProcess: DecisionMakerInvolvementOption;
  discoveryNoShowRatePercent: number | null;
  discoveryFrameworkStep3: YesNoUnknown;

  // Step 4: Pipeline & Governance
  pipelineTransparency: PipelineTransparencyOption;
  nextStepCoveragePercent: number | null;
  forecastAccuracy: ForecastAccuracyOption;
  pipelineReviewCadence: ReviewCadenceOption;
  crmUsage: CRMUsageOption;
  // Step 4 new fields
  stalePipelineSharePercent: number | null;
  forecastCoverageRatio: number | null;

  // Step 5: Lead Quality & Source
  pipelineSource: PipelineSourceOption;
  leadQuality: LeadQualityOption;
  icpDefined: YesNoUnknown;
  icpClarity: IcpClarityOption;
  marketPresence: MarketPresenceOption;
  // Step 5 new fields
  leadScoringMaturity: LeadScoringOption;
  qualifiedLeadRatePercent: number | null;

  // Step 5b: Akquise-Mix (NEW)
  acquisitionPhone: number | null;
  acquisitionEmail: number | null;
  acquisitionLinkedIn: number | null;
  acquisitionXing: number | null;
  acquisitionReferrals: number | null;
  acquisitionEvents: number | null;
  acquisitionContentMarketing: number | null;
  acquisitionPaidAds: number | null;
  acquisitionColdVisits: number | null;
  acquisitionPartner: number | null;
  acquisitionOther: number | null;

  // Step 6: Tools & Systems
  crmAvailable: YesNoUnknown;
  crmName: string;
  marketingAutomation: YesNoUnknown;
  leadRouting: YesNoUnknown;
  salesPlaybookAvailable: YesNoUnknown;
  // Step 7: Ziel & Engpass
  salesGoal: GoalOption;
  primaryPain: PainOption;

  // Step 8: Unternehmenskontext
  companyName: string;
  companyDomain: string; // NEW: Website domain for AI enrichment
  industry: string;
  companySize: CompanySizeOption;
  teamSize: number | null;
  businessModel: BusinessModelOption;

  // Step 9: Ansprechpartner
  contactFirstName: string;
  contactLastName: string;
  contactRole: string;
  contactEmail: string;
  contactPhone: string;
  wantsEmailResults: boolean;

  // NEW: Sales Rep Performance (reworked v2)
  productiveReps: number | null;

  avgRevenuePerRepVisibility: RevenueVisibilityOption;
  avgRevenuePerRep: number | null;

  totalRevenueTargetYear: number | null;
  revenueAchievedYTD: number | null;
  revenueTargetBrokenDownToReps: RevenueTargetBreakdownOption;

  repsHaveRevenueTarget: YesNoUnknown;
  avgRevenueTargetPerRep: number | null;

  repsOnTrackToTarget: RepsOnTrackOption;
  repPerformanceVariance: PerformanceVarianceOption;

  repsWithEnoughPipeline: RepsWithEnoughPipelineOption;
  pipelineConfidence: PipelineConfidenceOption;

  repReviewCadence: RepReviewCadenceOption;

  // ── "Nicht bekannt"-Flags für numerische Felder ──────────────────
  averageDealSize_unknown?: boolean;
  monthlyDeals_unknown?: boolean;
  monthlyContacts_unknown?: boolean;
  monthlyDiscoveryCalls_unknown?: boolean;
  monthlyPresentations_unknown?: boolean;
  salesCycleMonths_unknown?: boolean;
  monthlyRevenueOverride_unknown?: boolean;
  convLeadToDiscoveryUnknown?: boolean;
  convDiscoveryToOfferUnknown?: boolean;
  convOfferToCloseUnknown?: boolean;
  daysFromDiscoveryToProposal_unknown?: boolean;
  proposalsWithNextStepPercent_unknown?: boolean;
  timeToFirstMeetingDays_unknown?: boolean;
  avgFollowUpsPerLead_unknown?: boolean;
  decisionMakerInFirstMeetingPercent_unknown?: boolean;
  discoveryNoShowRatePercent_unknown?: boolean;
  nextStepCoveragePercent_unknown?: boolean;
  stalePipelineSharePercent_unknown?: boolean;
  forecastCoverageRatio_unknown?: boolean;
  qualifiedLeadRatePercent_unknown?: boolean;
  productiveReps_unknown?: boolean;
  avgRevenuePerRep_unknown?: boolean;
  totalRevenueTargetYear_unknown?: boolean;
  revenueAchievedYTD_unknown?: boolean;
  avgRevenueTargetPerRep_unknown?: boolean;
  // ── "Nicht bekannt"-Flags für Dropdown-Felder ──────────────────
  biggestDropoff_unknown?: boolean;
  touchpointsToClose_unknown?: boolean;
  stageExitCriteria_unknown?: boolean;
  responseTime_unknown?: boolean;
  followUpDiscipline_unknown?: boolean;
  followUpFrequency_unknown?: boolean;
  followUpOwnership_unknown?: boolean;
  lostLeadsNoResponse_unknown?: boolean;
  decisionMakerInProcess_unknown?: boolean;
  discoveryFrameworkStep3_unknown?: boolean;
  pipelineTransparency_unknown?: boolean;
  forecastAccuracy_unknown?: boolean;
  pipelineReviewCadence_unknown?: boolean;
  crmUsage_unknown?: boolean;
  pipelineSource_unknown?: boolean;
  leadQuality_unknown?: boolean;
  icpClarity_unknown?: boolean;
  leadScoringMaturity_unknown?: boolean;
  marketPresence_unknown?: boolean;
  crmAvailable_unknown?: boolean;
  marketingAutomation_unknown?: boolean;
  leadRouting_unknown?: boolean;
  salesPlaybookAvailable_unknown?: boolean;
  revenueTargetBrokenDownToReps_unknown?: boolean;
  repsHaveRevenueTarget_unknown?: boolean;
  repsOnTrackToTarget_unknown?: boolean;
  repPerformanceVariance_unknown?: boolean;
  repsWithEnoughPipeline_unknown?: boolean;
  pipelineConfidence_unknown?: boolean;
  repReviewCadence_unknown?: boolean;
  avgRevenuePerRepVisibility_unknown?: boolean;
  companySize_unknown?: boolean;
  businessModel_unknown?: boolean;
  // Legacy flags (already existed in answersMap)
  [key: string]: unknown;
};

export type DerivedAuditProfile = {
  funnelData: {
    averageDealSize: number;
    salesCycleMonths: number;
    monthlyRevenueBaseline: number;
    monthlyDeals: number;
    monthlyContacts: number;
    monthlyDiscoveryCalls: number;
    monthlyPresentations: number;
    responseTimeHours: number | null;
    timeToFirstMeetingDays: number | null;
    leadToDiscoveryConversionPct: number | null;
    discoveryToOfferConversionPct: number | null;
    offerToCloseConversionPct: number | null;
    nextStepCoveragePercent: number | null;
    followUpDiscipline: FollowUpDisciplineOption;
    pipelineTransparency: PipelineTransparencyOption;
    leadQuality: LeadQualityOption;
    // new funnelData fields
    daysFromDiscoveryToProposal: number | null;
    proposalsWithNextStepPercent: number | null;
    avgFollowUpsPerLead: number | null;
    decisionMakerInFirstMeetingPercent: number | null;
    discoveryNoShowRatePercent: number | null;
    stalePipelineSharePercent: number | null;
    forecastCoverageRatio: number | null;
    qualifiedLeadRatePercent: number | null;
  };
  companyContext: {
    companyName: string;
    industry: string;
    companySize: CompanySizeOption;
    teamSize: number | null;
    businessModel: BusinessModelOption;
  };
  contact: {
    fullName: string;
    role: string;
    email: string;
    phone: string;
    wantsEmailResults: boolean;
  };
  diagnosticInputs: {
    salesGoal: GoalOption;
    primaryPain: PainOption;
    biggestDropoff: BiggestDropoffOption;
    touchpointsToClose: TouchpointsToCloseOption;
    stageExitCriteria: YesNoUnknown;
    salesPlaybookAvailable: YesNoUnknown;
    forecastAccuracy: ForecastAccuracyOption;
    pipelineReviewCadence: ReviewCadenceOption;
    crmAvailable: YesNoUnknown;
    crmName: string;
    crmUsage: CRMUsageOption;
    marketingAutomation: YesNoUnknown;
    leadRouting: YesNoUnknown;
    pipelineSource: PipelineSourceOption;
    icpDefined: YesNoUnknown;
    followUpOwnership: OwnershipClarityOption;
    lostLeadsNoResponse: LostLeadsOption;
    // new diagnosticInputs fields
    decisionMakerInProcess: DecisionMakerInvolvementOption;
    leadScoringMaturity: LeadScoringOption;
    forecastCoverageRatio: number | null;
    icpClarity: IcpClarityOption;
    marketPresence: MarketPresenceOption;
  };
  dynamicSignals: {
    likelyPriority1:
      | "pipeline-governance"
      | "discovery-follow-up"
      | "lead-quality-top-of-funnel"
      | "conversion-system"
      | "forecast-structure"
      | "sales-cycle-complexity"
      | "speed-to-lead";
    likelyPriority2:
      | "pipeline-governance"
      | "discovery-follow-up"
      | "lead-quality-top-of-funnel"
      | "conversion-system"
      | "forecast-structure"
      | "sales-cycle-complexity"
      | "speed-to-lead";
    likelyQuickWins: string[];
    pressureNarrative: string;
    goalNarrative: string;
    painNarrative: string;
    architectureNarrative: string;
    readinessScore: number;
    segmentationLabel:
      | "volume-engine-broken"
      | "conversion-engine-broken"
      | "pipeline-chaos"
      | "speed-problem"
      | "forecast-problem"
      | "sales-cycle-friction"
      | "balanced-but-under-optimized";
    // new rootCauseCluster
    rootCauseCluster:
      | "speed-and-follow-up"
      | "discovery-quality"
      | "pipeline-hygiene"
      | "lead-quality"
      | "forecast-reliability"
      | "mixed"
      | "unknown";
  };
  salesRepPerformance: {
    productiveReps: number | null;

    avgRevenuePerRepVisibility: RevenueVisibilityOption;
    avgRevenuePerRep: number | null;

    totalRevenueTargetYear: number | null;
    revenueAchievedYTD: number | null;
    revenueTargetBrokenDownToReps: RevenueTargetBreakdownOption;

    repsHaveRevenueTarget: YesNoUnknown;
    avgRevenueTargetPerRep: number | null;

    repsOnTrackToTarget: RepsOnTrackOption;
    repPerformanceVariance: PerformanceVarianceOption;

    repsWithEnoughPipeline: RepsWithEnoughPipelineOption;
    pipelineConfidence: PipelineConfidenceOption;

    repReviewCadence: RepReviewCadenceOption;
  };
};

// -------------------------------------------------------
// CONFIG
// -------------------------------------------------------

const STEPS = [
  {
    id: "sales-output",
    title: "Sales Output",
    subtitle: "Die wirtschaftliche Grundlage Ihrer Analyse",
  },
  {
    id: "conversion-funnel",
    title: "Conversion & Funnel",
    subtitle: "Wo im Funnel tatsächlich Reibung entsteht",
  },
  {
    id: "discovery-execution",
    title: "Discovery & Execution",
    subtitle: "Wie schnell und konsequent Vertrieb wirklich arbeitet",
  },
  {
    id: "pipeline-governance",
    title: "Pipeline & Governance",
    subtitle: "Wie sauber Opportunities und Forecast gesteuert werden",
  },
  {
    id: "lead-quality-source",
    title: "Lead Quality & Source",
    subtitle: "Woher Pipeline kommt und wie nutzbar sie wirklich ist",
  },
  {
    id: "sales-rep-performance",
    title: "Sales Rep Performance",
    subtitle: "Zielerreichung, Produktivität und Pipeline pro Rep",
  },
  {
    id: "acquisition-mix",
    title: "Akquise-Mix",
    subtitle: "Wie verteilt sich Ihre Neukundengewinnung auf verschiedene Kanäle?",
  },
  {
    id: "tools-systems",
    title: "Tools & Systems",
    subtitle: "Welche Systeme den Prozess tragen oder bremsen",
  },
  {
    id: "goal-pain",
    title: "Ziel & Engpass",
    subtitle: "Was Sie erreichen wollen und was Sie aktuell blockiert",
  },
  {
    id: "company-context",
    title: "Unternehmenskontext",
    subtitle: "Benchmarking und kontextuelle Einordnung",
  },
  {
    id: "contact",
    title: "Ansprechpartner",
    subtitle: "Für Zustellung und spätere Einordnung",
  },
] as const;

const DEAL_SIZE_RANGES = [
  { value: "0-1000", label: "0 – 1.000 €" },
  { value: "1000-5000", label: "1.000 – 5.000 €" },
  { value: "5000-10000", label: "5.000 – 10.000 €" },
  { value: "10000-25000", label: "10.000 – 25.000 €" },
  { value: "25000-50000", label: "25.000 – 50.000 €" },
  { value: "50000+", label: "50.000 €+" },
];

const SALES_CYCLE_RANGES = [
  { value: "0-1", label: "unter 1 Monat" },
  { value: "1-3", label: "1 – 3 Monate" },
  { value: "3-6", label: "3 – 6 Monate" },
  { value: "6-12", label: "6 – 12 Monate" },
  { value: "12+", label: "12+ Monate" },
];

const CONVERSION_RANGE_OPTIONS: Array<{ value: ConversionRangeOption; label: string }> = [
  { value: "under-10", label: "unter 10%" },
  { value: "10-20", label: "10 – 20%" },
  { value: "20-30", label: "20 – 30%" },
  { value: "30-50", label: "30 – 50%" },
  { value: "50+", label: "50%+" },
];

const GOAL_OPTIONS: Array<{ value: GoalOption; label: string }> = [
  { value: "more-qualified-leads", label: "Mehr qualifizierte Leads" },
  { value: "higher-conversion", label: "Höhere Abschlussquote" },
  { value: "shorter-sales-cycle", label: "Kürzerer Sales Cycle" },
  { value: "better-forecast", label: "Planbarer Forecast" },
  { value: "revenue-growth", label: "Mehr Umsatzwachstum" },
  { value: "more-pipeline", label: "Mehr belastbare Pipeline" },
  { value: "not-known", label: "Noch offen / weiß nicht" },
];

const PAIN_OPTIONS: Array<{ value: PainOption; label: string }> = [
  { value: "too-few-leads", label: "Zu wenig Leads" },
  { value: "poor-lead-quality", label: "Schlechte Lead-Qualität" },
  { value: "too-few-discovery-calls", label: "Zu wenig Erstgespräche" },
  { value: "low-conversion", label: "Zu niedrige Conversion" },
  { value: "slow-response", label: "Zu langsame Reaktion auf Leads" },
  { value: "weak-follow-up", label: "Schwaches Follow-up" },
  { value: "missing-pipeline-structure", label: "Fehlende Pipeline-Struktur" },
  { value: "unreliable-forecast", label: "Unzuverlässiger Forecast" },
  { value: "long-sales-cycle", label: "Zu langer Sales Cycle" },
  { value: "low-win-rate", label: "Zu niedrige Win-Rate" },
  { value: "not-known", label: "Noch offen / weiß nicht" },
];

const RESPONSE_OPTIONS: Array<{ value: ResponseTimeOption; label: string }> = [
  { value: "under-1h", label: "Unter 1 Stunde" },
  { value: "1-3h", label: "1 – 3 Stunden" },
  { value: "same-day", label: "Am selben Tag" },
  { value: "1-3days", label: "1 – 3 Tage" },
  { value: "more-than-3days", label: "Mehr als 3 Tage" },
];

const FOLLOW_UP_OPTIONS: Array<{ value: FollowUpDisciplineOption; label: string }> = [
  { value: "very-structured", label: "Sehr strukturiert" },
  { value: "partly-structured", label: "Teilweise strukturiert" },
  { value: "ad-hoc", label: "Eher ad hoc" },
  { value: "hardly-any", label: "Kaum systematisch" },
];

const FOLLOW_UP_FREQUENCY_OPTIONS: Array<{ value: FollowUpFrequencyOption; label: string }> = [
  { value: "same-day", label: "Am selben Tag" },
  { value: "within-2days", label: "Innerhalb von 2 Tagen" },
  { value: "within-week", label: "Innerhalb einer Woche" },
  { value: "irregular", label: "Unregelmäßig" },
];

const LEAD_QUALITY_OPTIONS: Array<{ value: LeadQualityOption; label: string }> = [
  { value: "very-high", label: "Sehr hoch" },
  { value: "good", label: "Gut" },
  { value: "mixed", label: "Gemischt" },
  { value: "weak", label: "Eher schwach" },
];

const PIPELINE_OPTIONS: Array<{ value: PipelineTransparencyOption; label: string }> = [
  { value: "full", label: "Vollständig transparent" },
  { value: "partial", label: "Teilweise transparent" },
  { value: "low", label: "Eher gering" },
  { value: "minimal", label: "Kaum vorhanden" },
];

const YES_NO_UNKNOWN_OPTIONS: Array<{ value: YesNoUnknown; label: string }> = [
  { value: "yes", label: "Ja" },
  { value: "partly", label: "Teilweise" },
  { value: "no", label: "Nein" },
];

const SOURCE_OPTIONS: Array<{ value: PipelineSourceOption; label: string }> = [
  { value: "outbound", label: "Outbound / Akquise" },
  { value: "inbound", label: "Inbound / Marketing" },
  { value: "referrals", label: "Empfehlungen" },
  { value: "existing-customers", label: "Bestandskunden / Upsell" },
  { value: "partner-channel", label: "Partner / Channel" },
  { value: "mixed", label: "Mischmodell" },
];

const BIGGEST_DROPOFF_OPTIONS: Array<{ value: BiggestDropoffOption; label: string }> = [
  { value: "lead-to-discovery", label: "Lead → Discovery" },
  { value: "discovery-to-offer", label: "Discovery → Angebot" },
  { value: "offer-to-close", label: "Angebot → Abschluss" },
  { value: "unclear", label: "Unklar / nicht transparent" },
];

const TOUCHPOINT_OPTIONS: Array<{ value: TouchpointsToCloseOption; label: string }> = [
  { value: "1-2", label: "1 – 2" },
  { value: "3-5", label: "3 – 5" },
  { value: "6-10", label: "6 – 10" },
  { value: "10+", label: "10+" },
];

const FORECAST_ACCURACY_OPTIONS: Array<{ value: ForecastAccuracyOption; label: string }> = [
  { value: "very-accurate", label: "Sehr genau" },
  { value: "within-20-percent", label: "meist innerhalb ±20%" },
  { value: "unreliable", label: "Unzuverlässig" },
];

const REVIEW_CADENCE_OPTIONS: Array<{ value: ReviewCadenceOption; label: string }> = [
  { value: "weekly", label: "Wöchentlich" },
  { value: "biweekly", label: "Alle 2 Wochen" },
  { value: "monthly", label: "Monatlich" },
  { value: "rarely", label: "Selten" },
  { value: "never", label: "Praktisch nie" },
];

const CRM_USAGE_OPTIONS: Array<{ value: CRMUsageOption; label: string }> = [
  { value: "fully-used", label: "Vollständig genutzt" },
  { value: "partly-used", label: "Teilweise genutzt" },
  { value: "barely-used", label: "Kaum genutzt" },
];

const ICP_CLARITY_OPTIONS: Array<{ value: IcpClarityOption; label: string }> = [
  { value: "very-clear", label: "Sehr klar – ICP ist dokumentiert und wird aktiv genutzt" },
  { value: "mostly-clear", label: "Weitgehend klar – aber nicht formal dokumentiert" },
  { value: "vague", label: "Eher vage – grobe Vorstellungen vorhanden" },
  { value: "not-defined", label: "Nicht definiert – kein ICP vorhanden" },
];

const MARKET_PRESENCE_OPTIONS: Array<{ value: MarketPresenceOption; label: string }> = [
  { value: "strong", label: "Stark – wir sind in unserem Markt gut bekannt" },
  { value: "moderate", label: "Mittel – wir werden wahrgenommen, aber nicht als erste Wahl" },
  { value: "weak", label: "Schwach – wir sind noch kaum sichtbar" },
  { value: "unknown", label: "Unklar – wir wissen es nicht genau" },
];

const OWNERSHIP_OPTIONS: Array<{ value: OwnershipClarityOption; label: string }> = [
  { value: "clear", label: "Eindeutig geregelt" },
  { value: "partly-clear", label: "Teilweise geregelt" },
  { value: "unclear", label: "Unklar" },
];

const LOST_LEADS_OPTIONS: Array<{ value: LostLeadsOption; label: string }> = [
  { value: "rarely", label: "Selten" },
  { value: "regularly", label: "Regelmäßig" },
  { value: "often", label: "Häufig" },
];

// NEW: Sales Rep Performance Options (v2)
const REVENUE_VISIBILITY_OPTIONS: Array<{ value: RevenueVisibilityOption; label: string }> = [
  { value: "exact", label: "Genau bekannt" },
  { value: "estimated", label: "Grob geschätzt" },
  { value: "not-known", label: "Nicht bekannt" },
];

const REVENUE_TARGET_BREAKDOWN_OPTIONS: Array<{ value: RevenueTargetBreakdownOption; label: string }> = [
  { value: "yes", label: "Ja, klar heruntergebrochen" },
  { value: "partial", label: "Teilweise" },
  { value: "no", label: "Nein" },
];

const REPS_ON_TRACK_OPTIONS: Array<{ value: RepsOnTrackOption; label: string }> = [
  { value: "almost-all", label: "Fast alle" },
  { value: "more-than-half", label: "Mehr als die Hälfte" },
  { value: "less-than-half", label: "Weniger als die Hälfte" },
  { value: "hardly-any", label: "Kaum jemand" },
];

const PERFORMANCE_VARIANCE_OPTIONS: Array<{ value: PerformanceVarianceOption; label: string }> = [
  { value: "low", label: "Gering" },
  { value: "moderate", label: "Moderat" },
  { value: "high", label: "Groß" },
  { value: "very-high", label: "Sehr groß" },
];

const REPS_WITH_ENOUGH_PIPELINE_OPTIONS: Array<{ value: RepsWithEnoughPipelineOption; label: string }> = [
  { value: "almost-all", label: "Fast alle" },
  { value: "more-than-half", label: "Mehr als die Hälfte" },
  { value: "less-than-half", label: "Weniger als die Hälfte" },
  { value: "hardly-any", label: "Kaum jemand" },
];

const PIPELINE_CONFIDENCE_OPTIONS: Array<{ value: PipelineConfidenceOption; label: string }> = [
  { value: "very-high", label: "Sehr sicher" },
  { value: "rather-high", label: "Eher sicher" },
  { value: "rather-low", label: "Eher unsicher" },
  { value: "very-low", label: "Sehr unsicher" },
];

// DEAL_MOMENTUM_OPTIONS removed — dealMomentumLoss field removed from wizard

const REP_REVIEW_CADENCE_OPTIONS: Array<{ value: RepReviewCadenceOption; label: string }> = [
  { value: "weekly", label: "Wöchentlich" },
  { value: "biweekly", label: "Alle 2 Wochen" },
  { value: "monthly", label: "Monatlich" },
  { value: "rarely", label: "Selten" },
  { value: "never", label: "Nie" },
];

// NEW: Additional option arrays from patch spec
const DECISION_MAKER_INVOLVEMENT_OPTIONS: Array<{ value: DecisionMakerInvolvementOption; label: string }> = [
  { value: "first-meeting", label: "Schon im ersten Meeting" },
  { value: "mid-process", label: "In der Mitte des Prozesses" },
  { value: "late-process", label: "Erst kurz vor Abschluss" },
  { value: "never", label: "Praktisch nie" },
];

const LEAD_SCORING_OPTIONS: Array<{ value: LeadScoringOption; label: string }> = [
  { value: "formal", label: "Formales Lead Scoring vorhanden" },
  { value: "informal", label: "Informell / intuitiv" },
  { value: "none", label: "Kein Lead Scoring" },
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PROPOSAL_SPEED_OPTIONS: Array<{ value: ProposalSpeedOption; label: string }> = [
  { value: "same-day", label: "Am selben Tag" },
  { value: "1-3days", label: "1 – 3 Tage" },
  { value: "4-7days", label: "4 – 7 Tage" },
  { value: "over-week", label: "Über eine Woche" },
];

const PLAYBOOK_OPTIONS: Array<{ value: PlaybookOption; label: string }> = [
  { value: "comprehensive", label: "Ja, umfassend" },
  { value: "partial", label: "Teilweise" },
  { value: "none", label: "Nein" },
];

const RAMP_UP_OPTIONS: Array<{ value: RampUpOption; label: string }> = [
  { value: "under-1", label: "Unter 1 Monat" },
  { value: "1-3", label: "1 – 3 Monate" },
  { value: "3-6", label: "3 – 6 Monate" },
  { value: "6+", label: "Mehr als 6 Monate" },
];

const COMPANY_SIZE_OPTIONS: Array<{ value: CompanySizeOption; label: string }> = [
  { value: "1-10", label: "1 – 10 Mitarbeitende" },
  { value: "11-50", label: "11 – 50 Mitarbeitende" },
  { value: "51-200", label: "51 – 200 Mitarbeitende" },
  { value: "201-1000", label: "201 – 1.000 Mitarbeitende" },
  { value: "1000+", label: "1.000+ Mitarbeitende" },
];

const BUSINESS_MODEL_OPTIONS: Array<{ value: BusinessModelOption; label: string }> = [
  { value: "b2b-services", label: "B2B Services" },
  { value: "b2b-saas", label: "B2B SaaS" },
  { value: "b2b-industrial", label: "B2B Industrie / Manufacturing" },
  { value: "agency", label: "Agentur" },
  { value: "consulting", label: "Consulting" },
  { value: "ecommerce", label: "E-Commerce" },
  { value: "other", label: "Sonstiges" },
];

const DEFAULT_FORM: AuditFormData = {
  averageDealSize: null,
  averageDealSizeRange: null,
  salesCycleMonths: null,
  salesCycleRange: null,
  monthlyRevenueOverride: null,
  monthlyDeals: null,
  monthlyContacts: null,
  monthlyDiscoveryCalls: null,
  monthlyPresentations: null,
  conversionLeadToDiscovery: null,
  conversionLeadToDiscoveryRange: "not-known",
  conversionDiscoveryToOffer: null,
  conversionDiscoveryToOfferRange: "not-known",
  conversionOfferToClose: null,
  conversionOfferToCloseRange: "not-known",
  biggestDropoff: "not-known",
  touchpointsToClose: "not-known",
  stageExitCriteria: "not-known",
  // Step 2 new defaults
  daysFromDiscoveryToProposal: null,
  proposalsWithNextStepPercent: null,
  responseTime: "not-known",
  timeToFirstMeetingDays: null,
  followUpDiscipline: "not-known",
  followUpFrequency: "not-known",
  followUpOwnership: "not-known",
  lostLeadsNoResponse: "not-known",
  // Step 3 new defaults
  avgFollowUpsPerLead: null,
  decisionMakerInFirstMeetingPercent: null,
  decisionMakerInProcess: "not-known",
  discoveryNoShowRatePercent: null,
  discoveryFrameworkStep3: "not-known",
  pipelineTransparency: "not-known",
  nextStepCoveragePercent: null,
  forecastAccuracy: "not-known",
  pipelineReviewCadence: "not-known",
  crmUsage: "not-known",
  // Step 4 new defaults
  stalePipelineSharePercent: null,
  forecastCoverageRatio: null,
  pipelineSource: "not-known",
  leadQuality: "not-known",
  icpDefined: "not-known",
  icpClarity: "not-known",
  marketPresence: "not-known",
  // Step 5 new defaults
  leadScoringMaturity: "not-known",
  qualifiedLeadRatePercent: null,
  acquisitionPhone: null,
  acquisitionEmail: null,
  acquisitionLinkedIn: null,
  acquisitionXing: null,
  acquisitionReferrals: null,
  acquisitionEvents: null,
  acquisitionContentMarketing: null,
  acquisitionPaidAds: null,
  acquisitionColdVisits: null,
  acquisitionPartner: null,
  acquisitionOther: null,
  crmAvailable: "not-known",
  crmName: "",
  marketingAutomation: "not-known",
  leadRouting: "not-known",
  salesPlaybookAvailable: "not-known",
  salesGoal: "not-known",
  primaryPain: "not-known",
  companyName: "",
  companyDomain: "",
  industry: "",
  companySize: "not-known",
  teamSize: null,
  businessModel: "not-known",
  contactFirstName: "",
  contactLastName: "",
  contactRole: "",
  contactEmail: "",
  contactPhone: "",
  wantsEmailResults: true,
  // NEW: Sales Rep Performance defaults
  productiveReps: null,

  avgRevenuePerRepVisibility: "not-known",
  avgRevenuePerRep: null,

  totalRevenueTargetYear: null,
  revenueAchievedYTD: null,
  revenueTargetBrokenDownToReps: "not-known",

  repsHaveRevenueTarget: "not-known",
  avgRevenueTargetPerRep: null,

  repsOnTrackToTarget: "not-known",
  repPerformanceVariance: "not-known",

  repsWithEnoughPipeline: "not-known",
  pipelineConfidence: "not-known",

  repReviewCadence: "not-known",
};

// -------------------------------------------------------
// HELPERS
// -------------------------------------------------------

function cn(...classes: Array<string | boolean | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function mergeInitialData(initial?: Partial<AuditFormData>): AuditFormData {
  return { ...DEFAULT_FORM, ...(initial || {}) };
}

function parseRangeMidpoint(value: string | null, fallback = 0): number {
  if (!value) return fallback;
  if (value === "50000+") return 50000;
  if (value === "12+") return 12;
  const [min, max] = value.split("-").map(Number);
  if (Number.isFinite(min) && Number.isFinite(max)) return (min + max) / 2;
  return fallback;
}

function safeNum(value: number | null | undefined, fallback = 0): number {
  return Number.isFinite(value as number) ? (value as number) : fallback;
}

function responseTimeToHours(value: ResponseTimeOption): number | null {
  switch (value) {
    case "under-1h":
      return 1;
    case "1-3h":
      return 2;
    case "same-day":
      return 8;
    case "1-3days":
      return 48;
    case "more-than-3days":
      return 96;
    default:
      return null;
  }
}

function conversionRangeToMidpoint(value: ConversionRangeOption): number | null {
  switch (value) {
    case "under-10":
      return 7;
    case "10-20":
      return 15;
    case "20-30":
      return 25;
    case "30-50":
      return 40;
    case "50+":
      return 60;
    default:
      return null;
  }
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);
}

function getOverallCompletion(data: AuditFormData): number {
  const checks = [
    data.averageDealSize !== null || (data.averageDealSizeRange !== null && !data["averageDealSize_unknown"]),
    data.salesCycleMonths !== null || (data.salesCycleRange !== null && !data["salesCycle_unknown"]),
    data.monthlyDeals !== null,
    data.monthlyContacts !== null,
    data.monthlyPresentations !== null,
    true, // responseTime always valid (including "not-known")
    true, // followUpDiscipline always valid
    true, // leadQuality always valid
    true, // pipelineTransparency always valid
    true, // salesGoal always valid
    true, // primaryPain always valid
    data.companyName.trim().length > 0,
    data.contactEmail.trim().length > 0,
    true, // pipelineSource always valid
    true, // forecastAccuracy always valid
    true, // biggestDropoff always valid
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

function getStepCompletion(data: AuditFormData, stepIndex: number): number {
  switch (stepIndex) {
    case 0: {
      const checks = [
        data.averageDealSize !== null || (data.averageDealSizeRange !== null && !data["averageDealSize_unknown"]),
        data.salesCycleMonths !== null || (data.salesCycleRange !== null && !data["salesCycle_unknown"]),
        data.monthlyDeals !== null,
        data.monthlyContacts !== null,
        data.monthlyPresentations !== null,
      ];
      return Math.round((checks.filter(Boolean).length / checks.length) * 100);
    }
    case 1: {
      const checks = [
        data.conversionLeadToDiscovery !== null || data.conversionLeadToDiscoveryRange !== "not-known",
        data.conversionDiscoveryToOffer !== null || data.conversionDiscoveryToOfferRange !== "not-known",
        data.conversionOfferToClose !== null || data.conversionOfferToCloseRange !== "not-known",
        true, // biggestDropoff always valid
        true, // touchpointsToClose always valid
        true, // stageExitCriteria always valid
        data.daysFromDiscoveryToProposal !== null,
        data.proposalsWithNextStepPercent !== null,
      ];
      return Math.round((checks.filter(Boolean).length / checks.length) * 100);
    }
    case 2: {
      const checks = [
        true, // responseTime always valid
        true, // followUpDiscipline always valid
        true, // followUpFrequency always valid
        true, // followUpOwnership always valid
        true, // lostLeadsNoResponse always valid
        data.avgFollowUpsPerLead !== null,
        data.discoveryNoShowRatePercent !== null,
        data.decisionMakerInFirstMeetingPercent !== null,
        data.decisionMakerInProcess !== "not-known",
        data.discoveryFrameworkStep3 !== "not-known",
      ];
      return Math.round((checks.filter(Boolean).length / checks.length) * 100);
    }
    case 3: {
      const checks = [
        true, // pipelineTransparency always valid
        true, // forecastAccuracy always valid
        true, // pipelineReviewCadence always valid
        true, // crmUsage always valid
        data.stalePipelineSharePercent !== null,
        data.forecastCoverageRatio !== null,
        data.nextStepCoveragePercent !== null,
      ];
      return Math.round((checks.filter(Boolean).length / checks.length) * 100);
    }
    case 4: {
      // Step 4: Lead Quality & Source
      const checks = [
        true, // pipelineSource always valid
        true, // leadQuality always valid
        data.icpClarity !== "not-known" || !!data.icpClarity_unknown,
        data.leadScoringMaturity !== "not-known" || !!data.leadScoringMaturity_unknown,
        data.qualifiedLeadRatePercent !== null || !!data.qualifiedLeadRatePercent_unknown,
        data.marketPresence !== "not-known" || !!data.marketPresence_unknown,
      ];
      return Math.round((checks.filter(Boolean).length / checks.length) * 100);
    }
    case 5: {
      // Step 5: Sales Rep Performance
      const checks = [
        data.productiveReps !== null || !!data.productiveReps_unknown,
        data.totalRevenueTargetYear !== null || !!data.totalRevenueTargetYear_unknown,
        data.revenueAchievedYTD !== null || !!data.revenueAchievedYTD_unknown,
        data.revenueTargetBrokenDownToReps !== "not-known" || !!data.revenueTargetBrokenDownToReps_unknown,
        data.repPerformanceVariance !== "not-known" || !!data.repPerformanceVariance_unknown,
        data.repsWithEnoughPipeline !== "not-known" || !!data.repsWithEnoughPipeline_unknown,
        data.pipelineConfidence !== "not-known" || !!data.pipelineConfidence_unknown,
        data.repReviewCadence !== "not-known" || !!data.repReviewCadence_unknown,
      ];
      return Math.round((checks.filter(Boolean).length / checks.length) * 100);
    }
    case 6: {
      // Akquise-Mix validation
      const total =
        (data.acquisitionPhone ?? 0) +
        (data.acquisitionEmail ?? 0) +
        (data.acquisitionLinkedIn ?? 0) +
        (data.acquisitionXing ?? 0) +
        (data.acquisitionReferrals ?? 0) +
        (data.acquisitionEvents ?? 0) +
        (data.acquisitionContentMarketing ?? 0) +
        (data.acquisitionPaidAds ?? 0) +
        (data.acquisitionColdVisits ?? 0) +
        (data.acquisitionPartner ?? 0) +
        (data.acquisitionOther ?? 0);
      return total === 100 ? 100 : Math.round((total / 100) * 100);
    }
    case 7: {
      const checks = [
        true, // crmAvailable always valid
        true, // marketingAutomation always valid
        true, // leadRouting always valid
        true, // salesPlaybookAvailable always valid
      ];
      return Math.round((checks.filter(Boolean).length / checks.length) * 100);
    }
    case 8: {
      const checks = [true, true]; // salesGoal and primaryPain always valid
      return Math.round((checks.filter(Boolean).length / checks.length) * 100);
    }
    case 9: {
      const checks = [
        data.companyName.trim().length > 0,
        true, // companySize always valid
      ];
      return Math.round((checks.filter(Boolean).length / checks.length) * 100);
    }
    case 10: {
      const checks = [data.contactEmail.trim().length > 0];
      return Math.round((checks.filter(Boolean).length / checks.length) * 100);
    }
    default:
      return 0;
  }
}

function validateStep(data: AuditFormData, stepIndex: number): string[] {
  switch (stepIndex) {
    case 0: {
      const errors: string[] = [];
      if (data.averageDealSize === null && !data.averageDealSizeRange) {
        errors.push("Bitte geben Sie eine durchschnittliche Deal-Größe an.");
      }
      if (data.salesCycleMonths === null && !data.salesCycleRange) {
        errors.push("Bitte geben Sie einen Sales Cycle an.");
      }
      if (data.monthlyDeals === null) errors.push("Bitte geben Sie Abschlüsse pro Monat an.");
      if (data.monthlyContacts === null) errors.push("Bitte geben Sie Kontaktaufnahmen pro Monat an.");
      if (data.monthlyPresentations === null) errors.push("Bitte geben Sie Präsentationen/Demos pro Monat an.");
      return errors;
    }
    case 1: {
      // Step 2: Conversion & Funnel — Fix 4: Rates >100% abfangen
      const errors: string[] = [];
      if (data.conversionLeadToDiscovery !== null && (data.conversionLeadToDiscovery < 0 || data.conversionLeadToDiscovery > 100)) {
        errors.push("Conversion Lead → Discovery muss zwischen 0 und 100\u00a0% liegen.");
      }
      if (data.conversionDiscoveryToOffer !== null && (data.conversionDiscoveryToOffer < 0 || data.conversionDiscoveryToOffer > 100)) {
        errors.push("Conversion Discovery → Angebot muss zwischen 0 und 100\u00a0% liegen.");
      }
      if (data.conversionOfferToClose !== null && (data.conversionOfferToClose < 0 || data.conversionOfferToClose > 100)) {
        errors.push("Conversion Angebot → Abschluss muss zwischen 0 und 100\u00a0% liegen.");
      }
      if (data.proposalsWithNextStepPercent !== null && (data.proposalsWithNextStepPercent < 0 || data.proposalsWithNextStepPercent > 100)) {
        errors.push("Anteil Angebote mit Next Step muss zwischen 0 und 100\u00a0% liegen.");
      }
      return errors;
    }
    case 2: {
      // Speed, Discovery & Execution validation
      const errors: string[] = [];
      if (data.avgFollowUpsPerLead !== null && data.avgFollowUpsPerLead < 1) {
        errors.push("Die Anzahl der Follow-up-Versuche pro Lead muss mindestens 1 betragen.");
      }
      if (data.decisionMakerInFirstMeetingPercent !== null && (data.decisionMakerInFirstMeetingPercent < 0 || data.decisionMakerInFirstMeetingPercent > 100)) {
        errors.push("Entscheider-Anteil im Erstgespräch muss zwischen 0 und 100 % liegen.");
      }
      if (data.discoveryNoShowRatePercent !== null && (data.discoveryNoShowRatePercent < 0 || data.discoveryNoShowRatePercent > 100)) {
        errors.push("No-Show-Rate muss zwischen 0 und 100 % liegen.");
      }
      return errors;
    }
    case 3: {
      // Pipeline & Forecast Governance validation
      const errors: string[] = [];
      if (data.stalePipelineSharePercent !== null && (data.stalePipelineSharePercent < 0 || data.stalePipelineSharePercent > 100)) {
        errors.push("Anteil stagnierender Pipeline muss zwischen 0 und 100 % liegen.");
      }
      if (data.forecastCoverageRatio !== null && data.forecastCoverageRatio < 0) {
        errors.push("Forecast Coverage Ratio muss ein positiver Wert sein (z. B. 3,0 für 3-fache Abdeckung).");
      }
      return errors;
    }
    case 4: {
      // Step 4: Lead Quality & Source validation
      const errors: string[] = [];
      if (data.qualifiedLeadRatePercent !== null && (data.qualifiedLeadRatePercent < 0 || data.qualifiedLeadRatePercent > 100)) {
        errors.push("Qualifizierte Lead Rate muss zwischen 0 und 100 % liegen.");
      }
      // icpClarity: only error if NOT unknown-flagged and still "not-known"
      if (data.icpClarity === "not-known" && !data.icpClarity_unknown) {
        errors.push("Bitte geben Sie an, wie klar Ihr ICP definiert ist.");
      }
      // marketPresence: only error if NOT unknown-flagged and still "not-known"
      if (data.marketPresence === "not-known" && !data.marketPresence_unknown) {
        errors.push("Bitte geben Sie Ihre Marktpräsenz an.");
      }
      return errors;
    }
    case 5: {
      // Step 5: Sales Rep Performance validation
      const errors: string[] = [];

      if (data.productiveReps === null && !data.productiveReps_unknown) {
        errors.push("Bitte geben Sie die Anzahl produktiver Sales Reps an.");
      }

      if (data.totalRevenueTargetYear === null && !data.totalRevenueTargetYear_unknown) {
        errors.push("Bitte geben Sie das Umsatzziel für das laufende Jahr an.");
      }

      if (data.revenueAchievedYTD === null && !data.revenueAchievedYTD_unknown) {
        errors.push("Bitte geben Sie an, wie viel Umsatz in diesem Jahr bisher erzielt wurde.");
      }

      if (data.revenueTargetBrokenDownToReps === "not-known" && !data.revenueTargetBrokenDownToReps_unknown) {
        errors.push("Bitte geben Sie an, ob das Umsatzziel auf Reps heruntergebrochen ist.");
      }

      if (
        data.avgRevenuePerRepVisibility !== "not-known" &&
        !data.avgRevenuePerRepVisibility_unknown &&
        data.avgRevenuePerRep === null &&
        !data.avgRevenuePerRep_unknown
      ) {
        errors.push("Bitte geben Sie den durchschnittlichen Umsatz pro Rep an oder markieren Sie ihn als nicht bekannt.");
      }

      if (data.repsHaveRevenueTarget === "yes" && data.avgRevenueTargetPerRep === null && !data.avgRevenueTargetPerRep_unknown) {
        errors.push("Bitte geben Sie das monatliche Umsatzziel pro Rep an.");
      }

      if (data.repPerformanceVariance === "not-known" && !data.repPerformanceVariance_unknown) {
        errors.push("Bitte bewerten Sie, wie stark sich die Leistung der Reps unterscheidet.");
      }

      if (data.repsWithEnoughPipeline === "not-known" && !data.repsWithEnoughPipeline_unknown) {
        errors.push("Bitte bewerten Sie, wie viele Reps aktuell genug Pipeline haben.");
      }

      if (data.pipelineConfidence === "not-known" && !data.pipelineConfidence_unknown) {
        errors.push("Bitte bewerten Sie, wie sicher Ihre Pipeline zu Abschlüssen führt.");
      }

      if (data.repReviewCadence === "not-known" && !data.repReviewCadence_unknown) {
        errors.push("Bitte geben Sie an, wie regelmäßig Rep-Performance reviewed wird.");
      }

      return errors;
    }
    case 6: {
      // Akquise-Mix validation
      const errors: string[] = [];
      const total =
        (data.acquisitionPhone ?? 0) +
        (data.acquisitionEmail ?? 0) +
        (data.acquisitionLinkedIn ?? 0) +
        (data.acquisitionXing ?? 0) +
        (data.acquisitionReferrals ?? 0) +
        (data.acquisitionEvents ?? 0) +
        (data.acquisitionContentMarketing ?? 0) +
        (data.acquisitionPaidAds ?? 0) +
        (data.acquisitionColdVisits ?? 0) +
        (data.acquisitionPartner ?? 0) +
        (data.acquisitionOther ?? 0);
      if (total !== 100) {
        errors.push(`Die Summe muss genau 100% ergeben (aktuell: ${total}%)`);
      }
      return errors;
    }
    case 10: {
      const errors: string[] = [];
      if (!data.contactEmail.trim()) {
        errors.push("Bitte geben Sie eine E-Mail-Adresse an.");
      }
      return errors;
    }
    default:
      return [];
  }
}

export function buildDerivedAuditProfile(raw: AuditFormData): DerivedAuditProfile {
  const averageDealSize =
    raw.averageDealSize !== null
      ? raw.averageDealSize
      : parseRangeMidpoint(raw.averageDealSizeRange, 5000);

  const salesCycleMonths =
    raw.salesCycleMonths !== null
      ? raw.salesCycleMonths
      : parseRangeMidpoint(raw.salesCycleRange, 2);

  const monthlyDeals = safeNum(raw.monthlyDeals, 0);
  const monthlyContacts = safeNum(raw.monthlyContacts, 0);
  const monthlyDiscoveryCalls = safeNum(raw.monthlyDiscoveryCalls, 0);
  const monthlyPresentations = safeNum(raw.monthlyPresentations, 0);

  // Conversion-Rates: Priorität: exakt > Range > Branchenschnitt-Fallback (wenn nicht-bekannt markiert) > null
  // Branchen-Benchmarks: Lead→Discovery 30%, Discovery→Angebot 25%, Angebot→Abschluss 25%
  const leadToDiscoveryConversionPct = (() => {
    if (raw.conversionLeadToDiscovery !== null) return raw.conversionLeadToDiscovery;
    const fromRange = conversionRangeToMidpoint(raw.conversionLeadToDiscoveryRange);
    if (fromRange !== null) return fromRange;
    if (raw.convLeadToDiscoveryUnknown) return 30; // Branchenschnitt-Fallback
    return null;
  })();

  const discoveryToOfferConversionPct = (() => {
    if (raw.conversionDiscoveryToOffer !== null) return raw.conversionDiscoveryToOffer;
    const fromRange = conversionRangeToMidpoint(raw.conversionDiscoveryToOfferRange);
    if (fromRange !== null) return fromRange;
    if (raw.convDiscoveryToOfferUnknown) return 25; // Branchenschnitt-Fallback
    return null;
  })();

  const offerToCloseConversionPct = (() => {
    if (raw.conversionOfferToClose !== null) return raw.conversionOfferToClose;
    const fromRange = conversionRangeToMidpoint(raw.conversionOfferToCloseRange);
    if (fromRange !== null) return fromRange;
    if (raw.convOfferToCloseUnknown) return 25; // Branchenschnitt-Fallback
    return null;
  })();

  const responseTimeHours = responseTimeToHours(raw.responseTime);
  const timeToFirstMeetingDays =
    raw.timeToFirstMeetingDays !== null ? raw.timeToFirstMeetingDays : null;

  const monthlyRevenueBaseline =
    raw.monthlyRevenueOverride !== null
      ? raw.monthlyRevenueOverride
      : averageDealSize * monthlyDeals;

  const pipelineWeak =
    raw.pipelineTransparency === "minimal" ||
    raw.pipelineTransparency === "low" ||
    (raw.nextStepCoveragePercent !== null && raw.nextStepCoveragePercent < 50);

  const followUpWeak =
    raw.followUpDiscipline === "ad-hoc" ||
    raw.followUpDiscipline === "hardly-any" ||
    raw.followUpOwnership === "unclear" ||
    raw.lostLeadsNoResponse === "often";

  const responseWeak =
    raw.responseTime === "1-3days" ||
    raw.responseTime === "more-than-3days" ||
    (raw.timeToFirstMeetingDays !== null && raw.timeToFirstMeetingDays > 7);

  const leadWeak =
    raw.leadQuality === "mixed" ||
    raw.leadQuality === "weak" ||
    raw.icpDefined === "no" ||
    (raw.qualifiedLeadRatePercent !== null && raw.qualifiedLeadRatePercent < 30) ||
    raw.leadScoringMaturity === "none";

  const forecastWeak =
    raw.forecastAccuracy === "unreliable" ||
    raw.pipelineReviewCadence === "rarely" ||
    raw.pipelineReviewCadence === "never";

  const discoveryWeak =
    raw.salesPlaybookAvailable === "no" ||
    raw.salesPlaybookAvailable === "partly" ||
    raw.stageExitCriteria === "no" ||
    raw.discoveryFrameworkStep3 === "no" ||
    (raw.discoveryNoShowRatePercent !== null && raw.discoveryNoShowRatePercent > 20);

  const speedWeak =
    responseWeak ||
    (raw.avgFollowUpsPerLead !== null && raw.avgFollowUpsPerLead < 2) ||
    (raw.decisionMakerInFirstMeetingPercent !== null && raw.decisionMakerInFirstMeetingPercent < 30);

  const pipelineHygieneWeak =
    (raw.stalePipelineSharePercent !== null && raw.stalePipelineSharePercent > 30) ||
    (raw.forecastCoverageRatio !== null && raw.forecastCoverageRatio < 2.5);

  const longCycle =
    salesCycleMonths > 6 ||
    raw.touchpointsToClose === "10+" ||
    raw.primaryPain === "long-sales-cycle";

  const lowCloseRate =
    offerToCloseConversionPct !== null && offerToCloseConversionPct < 20;

  const lowEarlyConversion =
    leadToDiscoveryConversionPct !== null && leadToDiscoveryConversionPct < 15;

  let likelyPriority1: DerivedAuditProfile["dynamicSignals"]["likelyPriority1"] =
    "conversion-system";

  if (responseWeak && (raw.primaryPain === "slow-response" || lowEarlyConversion)) {
    likelyPriority1 = "speed-to-lead";
  } else if (pipelineWeak || forecastWeak || raw.primaryPain === "missing-pipeline-structure") {
    likelyPriority1 = "pipeline-governance";
  } else if (
    raw.primaryPain === "unreliable-forecast" ||
    raw.forecastAccuracy === "unreliable"
  ) {
    likelyPriority1 = "forecast-structure";
  } else if (
    responseWeak ||
    followUpWeak ||
    discoveryWeak ||
    raw.primaryPain === "weak-follow-up"
  ) {
    likelyPriority1 = "discovery-follow-up";
  } else if (
    leadWeak ||
    raw.primaryPain === "poor-lead-quality" ||
    raw.primaryPain === "too-few-leads"
  ) {
    likelyPriority1 = "lead-quality-top-of-funnel";
  } else if (longCycle) {
    likelyPriority1 = "sales-cycle-complexity";
  }

  let likelyPriority2: DerivedAuditProfile["dynamicSignals"]["likelyPriority2"] =
    "conversion-system";

  const priorityPool: Array<DerivedAuditProfile["dynamicSignals"]["likelyPriority1"]> = [
    "pipeline-governance",
    "discovery-follow-up",
    "lead-quality-top-of-funnel",
    "conversion-system",
    "forecast-structure",
    "sales-cycle-complexity",
    "speed-to-lead",
  ];

  likelyPriority2 =
    priorityPool.find((item) => item !== likelyPriority1 && (
      (item === "pipeline-governance" && pipelineWeak) ||
      (item === "discovery-follow-up" && (followUpWeak || discoveryWeak)) ||
      (item === "lead-quality-top-of-funnel" && leadWeak) ||
      (item === "conversion-system" && (lowCloseRate || raw.biggestDropoff === "offer-to-close")) ||
      (item === "forecast-structure" && forecastWeak) ||
      (item === "sales-cycle-complexity" && longCycle) ||
      (item === "speed-to-lead" && responseWeak)
    )) || "conversion-system";

  const likelyQuickWins: string[] = [];

  if (responseWeak) {
    likelyQuickWins.push("Reaktionszeit standardisieren und Erstreaktion verbindlich beschleunigen");
  }
  if (raw.nextStepCoveragePercent !== null && raw.nextStepCoveragePercent < 70) {
    likelyQuickWins.push("Next-Step-Pflicht für jede offene Opportunity im CRM einführen");
  }
  if (followUpWeak) {
    likelyQuickWins.push("Follow-up-Playbook mit festen Kontaktregeln und Zeitfenstern einführen");
  }
  if (leadWeak) {
    likelyQuickWins.push("Lead-Scoring und härtere Qualifizierung im Erstgespräch verankern");
  }
  if (forecastWeak) {
    likelyQuickWins.push("Wöchentliche Pipeline- und Forecast-Reviews mit klaren Kriterien etablieren");
  }
  if (lowCloseRate) {
    likelyQuickWins.push("Angebots- und Closing-Phase strukturiert auf Einwände und Verbindlichkeit trimmen");
  }
  if (longCycle) {
    likelyQuickWins.push("Sales Cycle durch klare Exit-Kriterien und definierte Übergänge verkürzen");
  }

  const dedupedQuickWins = Array.from(new Set(likelyQuickWins)).slice(0, 4);

  const readinessSignals = [
    raw.responseTime !== "not-known",
    raw.followUpDiscipline !== "not-known",
    raw.leadQuality !== "not-known",
    raw.pipelineTransparency !== "not-known",
    raw.salesGoal !== "not-known",
    raw.primaryPain !== "not-known",
    raw.companyName.trim().length > 0,
    raw.pipelineSource !== "not-known",
    raw.forecastAccuracy !== "not-known",
    raw.biggestDropoff !== "not-known",
    raw.crmAvailable !== "not-known",
  ];

  const readinessScore = Math.round(
    (readinessSignals.filter(Boolean).length / readinessSignals.length) * 100
  );

  let segmentationLabel: DerivedAuditProfile["dynamicSignals"]["segmentationLabel"] =
    "balanced-but-under-optimized";

  if (pipelineWeak && forecastWeak) segmentationLabel = "pipeline-chaos";
  else if (responseWeak || raw.primaryPain === "slow-response") segmentationLabel = "speed-problem";
  else if (leadWeak && raw.pipelineSource !== "existing-customers") segmentationLabel = "volume-engine-broken";
  else if (lowCloseRate || raw.biggestDropoff === "offer-to-close") segmentationLabel = "conversion-engine-broken";
  else if (forecastWeak) segmentationLabel = "forecast-problem";
  else if (longCycle) segmentationLabel = "sales-cycle-friction";

  const goalNarrativeMap: Record<GoalOption, string> = {
    "more-qualified-leads":
      "Ihr Ziel ist mehr qualifizierter Pipeline-Zufluss. Ergebnis und Handlungsempfehlung sollten deshalb Lead-Qualität, Quelle, Reaktionszeit und Discovery klar verdichten.",
    "higher-conversion":
      "Ihr Ziel ist eine höhere Conversion. Ergebnis und Handlungsempfehlung sollten deshalb Funnel-Brüche, Qualifizierung, Angebotsphase und Follow-up besonders hart priorisieren.",
    "shorter-sales-cycle":
      "Ihr Ziel ist ein kürzerer Sales Cycle. Ergebnis und Handlungsempfehlung sollten deshalb Reaktionsgeschwindigkeit, Exit-Kriterien, Touchpoints und Next Steps fokussieren.",
    "better-forecast":
      "Ihr Ziel ist ein planbarer Forecast. Ergebnis und Handlungsempfehlung sollten deshalb Pipeline-Governance, Stage-Standards, Review-Rhythmus und Deal-Hygiene dominieren.",
    "revenue-growth":
      "Ihr Ziel ist Umsatzwachstum. Ergebnis und Handlungsempfehlung sollten deshalb die größten Umsatzverluste, Hebel und Prioritäten maximal klar verdichten.",
    "more-pipeline":
      "Ihr Ziel ist mehr belastbare Pipeline. Ergebnis und Handlungsempfehlung sollten deshalb Lead-Zufluss, Qualifizierung und Übergabequalität priorisieren.",
    "not-known":
      "Ihr Ziel ist noch nicht sauber definiert. Ergebnis und Handlungsempfehlung sollten deshalb die stärksten datenbasierten Hebel zuerst priorisieren.",
  };

  const painNarrativeMap: Record<PainOption, string> = {
    "too-few-leads":
      "Der größte Engpass liegt vermutlich im Top-of-Funnel. Die spätere Logik sollte Quelle, Lead-Qualität und frühe Conversion priorisieren.",
    "poor-lead-quality":
      "Der größte Engpass liegt vermutlich in der Nutzbarkeit der eingehenden Chancen. Die spätere Logik sollte ICP, Qualifizierung und Lead-Scoring priorisieren.",
    "too-few-discovery-calls":
      "Der größte Engpass liegt vermutlich zwischen Lead und Erstgespräch. Die spätere Logik sollte Reaktion, Terminquote und Ownership priorisieren.",
    "low-conversion":
      "Der größte Engpass liegt vermutlich in einem oder mehreren Funnel-Übergängen. Die spätere Logik sollte Drop-off, Gesprächsqualität und Angebotsphase fokussieren.",
    "slow-response":
      "Der größte Engpass liegt vermutlich in der Geschwindigkeit. Die spätere Logik sollte Response-SLA und frühe Disziplin priorisieren.",
    "weak-follow-up":
      "Der größte Engpass liegt vermutlich im Follow-up. Die spätere Logik sollte Kontaktsequenzen, Ownership und Next-Step-Verbindlichkeit priorisieren.",
    "missing-pipeline-structure":
      "Der größte Engpass liegt vermutlich in der Pipeline-Struktur. Die spätere Logik sollte Governance, Stage-Kriterien und Deal-Hygiene priorisieren.",
    "unreliable-forecast":
      "Der größte Engpass liegt vermutlich im Forecast. Die spätere Logik sollte Steuerung, Pipeline-Qualität und Review-Routinen priorisieren.",
    "long-sales-cycle":
      "Der größte Engpass liegt vermutlich in Reibung, Komplexität oder fehlender Klarheit im Verlauf des Deals. Die spätere Logik sollte Touchpoints, Exit-Kriterien und Übergänge priorisieren.",
    "low-win-rate":
      "Der größte Engpass liegt vermutlich in der späten Conversion. Die spätere Logik sollte Angebots- und Closing-Phase stärker verdichten.",
    "not-known":
      "Der größte Engpass ist noch nicht eindeutig definiert. Die spätere Logik sollte die stärksten datenbasierten Hebel priorisieren.",
  };

  // rootCauseCluster: synthesise weak-signal groups into a dominant theme
  let rootCauseCluster: DerivedAuditProfile["dynamicSignals"]["rootCauseCluster"] = "unknown";
  const clusterScores: Record<string, number> = {
    "speed-and-follow-up": (speedWeak ? 2 : 0) + (followUpWeak ? 2 : 0),
    "discovery-quality": (discoveryWeak ? 2 : 0),
    "pipeline-hygiene": (pipelineWeak ? 2 : 0) + (pipelineHygieneWeak ? 1 : 0),
    "lead-quality": (leadWeak ? 2 : 0),
    "forecast-reliability": (forecastWeak ? 2 : 0),
  };
  const topCluster = Object.entries(clusterScores).sort((a, b) => b[1] - a[1]);
  const topScore = topCluster[0][1];
  const secondScore = topCluster[1][1];
  if (topScore === 0) {
    rootCauseCluster = "unknown";
  } else if (topScore === secondScore && topScore > 0) {
    rootCauseCluster = "mixed";
  } else {
    rootCauseCluster = topCluster[0][0] as DerivedAuditProfile["dynamicSignals"]["rootCauseCluster"];
  }

  const pressureNarrative =
    pipelineWeak || responseWeak || followUpWeak || leadWeak || forecastWeak
      ? "Mehrere operative Vertriebshebel wirken aktuell gleichzeitig bremsend. Das spätere Ergebnis sollte klar zeigen, wo Umsatz, Geschwindigkeit, Qualität und Planbarkeit gleichzeitig verloren gehen."
      : "Die operative Basis wirkt teilweise stabil, aber das spätere Ergebnis sollte die größten Hebel sauber priorisieren und wirtschaftlich verdichten.";

  const architectureNarrative =
    raw.pipelineSource === "outbound"
      ? "Ihre Revenue-Architektur hängt stark an der Qualität von Outbound, schneller Reaktion und sauberer Übergabe in Discovery und Pipeline."
      : raw.pipelineSource === "inbound"
      ? "Ihre Revenue-Architektur hängt stark an Lead-Qualität, Routing, schneller Reaktion und sauberer Qualifizierung ab."
      : raw.pipelineSource === "referrals"
      ? "Ihre Revenue-Architektur hängt stark daran, Empfehlungen systematisch zu qualifizieren, sauber zu führen und in planbare Pipeline zu überführen."
      : "Ihre Revenue-Architektur sollte als Gesamtsystem aus Quelle, Reaktion, Discovery, Angebot, Pipeline und Forecast bewertet werden.";

  return {
    funnelData: {
      averageDealSize,
      salesCycleMonths,
      monthlyRevenueBaseline,
      monthlyDeals,
      monthlyContacts,
      monthlyDiscoveryCalls,
      monthlyPresentations,
      responseTimeHours,
      timeToFirstMeetingDays,
      leadToDiscoveryConversionPct,
      discoveryToOfferConversionPct,
      offerToCloseConversionPct,
      nextStepCoveragePercent: raw.nextStepCoveragePercent,
      followUpDiscipline: raw.followUpDiscipline,
      pipelineTransparency: raw.pipelineTransparency,
      leadQuality: raw.leadQuality,
      // new funnelData fields
      daysFromDiscoveryToProposal: raw.daysFromDiscoveryToProposal,
      proposalsWithNextStepPercent: raw.proposalsWithNextStepPercent,
      avgFollowUpsPerLead: raw.avgFollowUpsPerLead,
      decisionMakerInFirstMeetingPercent: raw.decisionMakerInFirstMeetingPercent,
      discoveryNoShowRatePercent: raw.discoveryNoShowRatePercent,
      stalePipelineSharePercent: raw.stalePipelineSharePercent,
      forecastCoverageRatio: raw.forecastCoverageRatio,
      qualifiedLeadRatePercent: raw.qualifiedLeadRatePercent,
    },
    companyContext: {
      companyName: raw.companyName,
      industry: raw.industry,
      companySize: raw.companySize,
      teamSize: raw.teamSize,
      businessModel: raw.businessModel,
    },
    contact: {
      fullName: [raw.contactFirstName, raw.contactLastName].filter(Boolean).join(" ").trim(),
      role: raw.contactRole,
      email: raw.contactEmail,
      phone: raw.contactPhone,
      wantsEmailResults: raw.wantsEmailResults,
    },
    diagnosticInputs: {
      salesGoal: raw.salesGoal,
      primaryPain: raw.primaryPain,
      biggestDropoff: raw.biggestDropoff,
      touchpointsToClose: raw.touchpointsToClose,
      stageExitCriteria: raw.stageExitCriteria,
      salesPlaybookAvailable: raw.salesPlaybookAvailable,
      forecastAccuracy: raw.forecastAccuracy,
      pipelineReviewCadence: raw.pipelineReviewCadence,
      crmAvailable: raw.crmAvailable,
      crmName: raw.crmName,
      crmUsage: raw.crmUsage,
      marketingAutomation: raw.marketingAutomation,
      leadRouting: raw.leadRouting,
      pipelineSource: raw.pipelineSource,
      icpDefined: raw.icpDefined,
      followUpOwnership: raw.followUpOwnership,
      lostLeadsNoResponse: raw.lostLeadsNoResponse,
      // new diagnosticInputs
      decisionMakerInProcess: raw.decisionMakerInProcess,
      leadScoringMaturity: raw.leadScoringMaturity,
      forecastCoverageRatio: raw.forecastCoverageRatio,
      icpClarity: raw.icpClarity,
      marketPresence: raw.marketPresence,
    },
    dynamicSignals: {
      likelyPriority1,
      likelyPriority2,
      likelyQuickWins: dedupedQuickWins,
      pressureNarrative,
      goalNarrative: goalNarrativeMap[raw.salesGoal],
      painNarrative: painNarrativeMap[raw.primaryPain],
      architectureNarrative,
      readinessScore,
      segmentationLabel,
      rootCauseCluster,
    },
    salesRepPerformance: {
      productiveReps: raw.productiveReps,

      avgRevenuePerRepVisibility: raw.avgRevenuePerRepVisibility,
      avgRevenuePerRep: raw.avgRevenuePerRep,

      totalRevenueTargetYear: raw.totalRevenueTargetYear,
      revenueAchievedYTD: raw.revenueAchievedYTD,
      revenueTargetBrokenDownToReps: raw.revenueTargetBrokenDownToReps,

      repsHaveRevenueTarget: raw.repsHaveRevenueTarget,
      avgRevenueTargetPerRep: raw.avgRevenueTargetPerRep,

      repsOnTrackToTarget: raw.repsOnTrackToTarget,
      repPerformanceVariance: raw.repPerformanceVariance,

      repsWithEnoughPipeline: raw.repsWithEnoughPipeline,
      pipelineConfidence: raw.pipelineConfidence,

      repReviewCadence: raw.repReviewCadence,
    },
  };
}

// -------------------------------------------------------
// UI PRIMITIVES
// -------------------------------------------------------

function FieldLabel({
  children,
  required = false,
  tooltip,
}: {
  children: React.ReactNode;
  required?: boolean;
  tooltip?: string;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  
  return (
    <div className="flex items-center gap-2 mb-2">
      <label className="text-sm font-semibold">
        {children}
        {required ? <span className="text-destructive ml-1">*</span> : null}
      </label>
      {tooltip && (
        <div className="relative">
          <button
            type="button"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={() => setShowTooltip(!showTooltip)}
            className="p-1 rounded-full hover:bg-muted transition-colors"
          >
            <HelpCircle className="w-4 h-4 text-muted-foreground" />
          </button>
          {showTooltip && (
            <div className="absolute left-0 top-full mt-2 w-72 p-3 bg-popover border rounded-lg shadow-lg text-sm text-popover-foreground z-50">
              {tooltip}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FieldHint({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-muted-foreground mt-1">{children}</p>;
}

function NumberInput({
  value,
  onChange,
  placeholder,
  min = 0,
  max,
  suffix,
  unknownId,
  unknownChecked,
  onUnknownChange,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  suffix?: string;
  /** Wenn gesetzt, erscheint eine "Nicht bekannt"-Checkbox */
  unknownId?: string;
  unknownChecked?: boolean;
  onUnknownChange?: (checked: boolean) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="relative">
        <input
          type="number"
          min={min}
          max={max}
          value={value === null ? "" : value}
          disabled={unknownChecked}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") return onChange(null);
            const num = Number(raw);
            onChange(Number.isFinite(num) ? num : null);
          }}
          placeholder={unknownChecked ? "Nicht bekannt" : placeholder}
          className={`w-full rounded-xl border px-4 py-3 outline-none transition-colors ${
            unknownChecked
              ? "bg-amber-50 border-amber-200 text-amber-400 cursor-not-allowed"
              : "bg-background focus:border-primary"
          }`}
        />
        {suffix && !unknownChecked ? (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
            {suffix}
          </span>
        ) : null}
      </div>
      {unknownId && onUnknownChange ? (
        <label className="flex items-center gap-2 cursor-pointer select-none group w-fit">
          <div
            onClick={() => onUnknownChange(!unknownChecked)}
            className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
              unknownChecked
                ? "bg-amber-500 border-amber-500"
                : "border-slate-300 bg-white group-hover:border-amber-400"
            }`}
          >
            {unknownChecked && (
              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                <path d="M10 3L5 8.5 2 5.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
          <span className={`text-xs transition-colors ${
            unknownChecked ? "text-amber-600 font-medium" : "text-muted-foreground group-hover:text-amber-600"
          }`}>
            Nicht bekannt
          </span>
        </label>
      ) : null}
    </div>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: React.HTMLInputTypeAttribute;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-xl border bg-background px-4 py-3 outline-none focus:border-primary transition-colors"
    />
  );
}

function SelectInput<T extends string>({
  value,
  onChange,
  options,
  unknownId,
  unknownChecked,
  onUnknownChange,
}: {
  value: T;
  onChange: (value: T) => void;
  options: Array<{ value: T; label: string }>;
  /** Wenn gesetzt, erscheint eine "Nicht bekannt"-Checkbox */
  unknownId?: string;
  unknownChecked?: boolean;
  onUnknownChange?: (checked: boolean) => void;
}) {
  // Wenn der Wert "not-known" ist aber keine Checkbox angehakt → ersten validen Wert setzen
  const validOptions = options.filter(o => o.value !== "not-known");
  const effectiveValue = (!unknownChecked && value === ("not-known" as T) && validOptions.length > 0)
    ? validOptions[0].value
    : value;

  // Sync: Falls value=="not-known" und !unknownChecked → sofort den ersten gültigen Wert propagieren
  React.useEffect(() => {
    if (!unknownChecked && value === ("not-known" as T) && validOptions.length > 0) {
      onChange(validOptions[0].value);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-2">
      <select
        value={unknownChecked ? "" : effectiveValue}
        disabled={unknownChecked}
        onChange={(e) => onChange(e.target.value as T)}
        className={`w-full rounded-xl border px-4 py-3 outline-none focus:border-primary transition-colors ${
          unknownChecked
            ? "bg-amber-50 border-amber-200 text-amber-400 cursor-not-allowed"
            : "bg-background"
        }`}
      >
        {unknownChecked ? (
          <option value="">Nicht bekannt</option>
        ) : (
          validOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))
        )}
      </select>
      {unknownId && onUnknownChange ? (
        <label className="flex items-center gap-2 cursor-pointer select-none group w-fit">
          <div
            onClick={() => onUnknownChange(!unknownChecked)}
            className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer ${
              unknownChecked
                ? "bg-amber-500 border-amber-500"
                : "border-slate-300 bg-white group-hover:border-amber-400"
            }`}
          >
            {unknownChecked && (
              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                <path d="M10 3L5 8.5 2 5.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
          <span className={`text-xs transition-colors ${
            unknownChecked ? "text-amber-600 font-medium" : "text-muted-foreground group-hover:text-amber-600"
          }`}>
            Nicht bekannt
          </span>
        </label>
      ) : null}
    </div>
  );
}

function OptionCard<T extends string>({
  value,
  current,
  label,
  onSelect,
}: {
  value: T;
  current: T;
  label: string;
  onSelect: (value: T) => void;
}) {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={cn(
        "rounded-2xl border p-4 text-left transition-all w-full",
        active
          ? "border-primary bg-primary/5 shadow-sm"
          : "border-border bg-background hover:border-primary/40"
      )}
    >
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}

function SectionBox({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-bold mb-1">{title}</h3>
        {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
      </div>
      {children}
    </div>
  );
}

// -------------------------------------------------------
// MAIN COMPONENT
// -------------------------------------------------------

type SalesAuditWizardMasterProps = {
  initialData?: Partial<AuditFormData>;
  onSubmit?: (payload: { raw: AuditFormData; normalized: DerivedAuditProfile }) => void;
  onChange?: (payload: { raw: AuditFormData; normalized: DerivedAuditProfile }) => void;
  submitLabel?: string;
};

export default function SalesAuditWizardMaster({
  initialData,
  onSubmit,
  onChange,
  submitLabel = "Audit auswerten",
}: SalesAuditWizardMasterProps) {
  const [form, setForm] = useState(() => mergeInitialData(initialData));
  const [currentStep, setCurrentStep] = useState(0);
  const [showErrors, setShowErrors] = useState(false);

  const normalized = useMemo(() => buildDerivedAuditProfile(form), [form]);
  const overallCompletion = useMemo(() => getOverallCompletion(form), [form]);
  const stepCompletion = useMemo(() => getStepCompletion(form, currentStep), [form, currentStep]);
  const currentErrors = useMemo(() => validateStep(form, currentStep), [form, currentStep]);

  // Fix 5: Datenlücken-Zähler für letzten Step
  const unknownFieldCount = useMemo(() => [
    form.averageDealSize_unknown, form.monthlyDeals_unknown,
    form.monthlyContacts_unknown, form.salesCycleMonths_unknown,
    form.convLeadToDiscoveryUnknown, form.convDiscoveryToOfferUnknown, form.convOfferToCloseUnknown,
    form.timeToFirstMeetingDays_unknown, form.avgFollowUpsPerLead_unknown,
    form.stalePipelineSharePercent_unknown, form.forecastCoverageRatio_unknown,
    form.qualifiedLeadRatePercent_unknown,
  ].filter(Boolean).length, [form]);

  useEffect(() => {
    onChange?.({ raw: form, normalized });
  }, [form, normalized, onChange]);

  function update<K extends keyof AuditFormData>(key: K, value: AuditFormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function nextStep() {
    const errors = validateStep(form, currentStep);
    if (errors.length > 0) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function prevStep() {
    setShowErrors(false);
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errors = validateStep(form, currentStep);
    if (errors.length > 0) {
      setShowErrors(true);
      return;
    }
    onSubmit?.({ raw: form, normalized });
  }

  function autofillTestData() {
    // ── Zufalls-Szenarien ────────────────────────────────────────────
    function rnd<T>(arr: T[]): T { return arr[Math.floor(Math.random() * arr.length)]; }
    function rndInt(min: number, max: number): number { return Math.floor(Math.random() * (max - min + 1)) + min; }
    function rndFloat(min: number, max: number, decimals = 1): number {
      const val = Math.random() * (max - min) + min;
      return Math.round(val * 10 ** decimals) / 10 ** decimals;
    }
    function maybe(prob: number): boolean { return Math.random() < prob; }

    // Szenario zufällig wählen (beeinflusst Gesamt-Qualität)
    type Scenario = "growth-winner" | "stuck-mid" | "crisis" | "blind-spots" | "scale-up";
    const scenario: Scenario = rnd<Scenario>(["growth-winner", "stuck-mid", "crisis", "blind-spots", "scale-up"]);

    // Szenario-Parameter
    const scenarioConfig = {
      "growth-winner": {
        label: "Wachstums-Champion", contacts: [200, 400], deals: [20, 40], dealSize: [12000, 30000],
        cycle: [1.5, 3], repCount: [8, 15], targetYear: [3000000, 6000000], achieved: [0.5, 0.75],
        c2d: [8, 15], d2p: [50, 70], p2d: [35, 55],
      },
      "stuck-mid": {
        label: "Plateau-Vertrieb", contacts: [80, 150], deals: [5, 12], dealSize: [8000, 20000],
        cycle: [2, 5], repCount: [3, 7], targetYear: [800000, 1500000], achieved: [0.25, 0.5],
        c2d: [4, 10], d2p: [30, 55], p2d: [20, 35],
      },
      "crisis": {
        label: "Kritische Lage", contacts: [40, 80], deals: [2, 6], dealSize: [5000, 15000],
        cycle: [4, 8], repCount: [2, 5], targetYear: [500000, 1000000], achieved: [0.1, 0.3],
        c2d: [2, 6], d2p: [20, 40], p2d: [10, 25],
      },
      "blind-spots": {
        label: "Viele Datenlücken", contacts: [60, 120], deals: [4, 10], dealSize: [10000, 25000],
        cycle: [2, 6], repCount: [3, 8], targetYear: [700000, 1800000], achieved: [0.2, 0.45],
        c2d: [5, 12], d2p: [35, 60], p2d: [20, 40],
      },
      "scale-up": {
        label: "Scale-up mit Struktur-Problemen", contacts: [150, 350], deals: [15, 30], dealSize: [15000, 50000],
        cycle: [3, 6], repCount: [10, 25], targetYear: [4000000, 10000000], achieved: [0.3, 0.55],
        c2d: [5, 10], d2p: [40, 65], p2d: [25, 45],
      },
    }[scenario];

    // "Nicht bekannt"-Flags: im Szenario "blind-spots" häufiger, sonst sporadisch
    const unknownProbability = scenario === "blind-spots" ? 0.6 : 0.2;
    const unknownC2D = maybe(unknownProbability);
    const unknownD2P = maybe(unknownProbability);
    const unknownP2D = maybe(unknownProbability);
    const unknownContacts = scenario === "blind-spots" ? maybe(0.4) : false;
    const unknownPresentations = maybe(unknownProbability * 0.5);
    const unknownDeals = false; // Fast nie unbekannt – Kernmetrik
    const unknownStale = maybe(unknownProbability);
    const unknownCoverage = maybe(unknownProbability);
    const unknownFollowUps = maybe(unknownProbability * 0.5);

    // Werte generieren
    const contacts = unknownContacts ? null : rndInt(scenarioConfig.contacts[0], scenarioConfig.contacts[1]);
    const deals = rndInt(scenarioConfig.deals[0], scenarioConfig.deals[1]);
    const dealSize = rndInt(scenarioConfig.dealSize[0], scenarioConfig.dealSize[1]);
    const cycle = rndFloat(scenarioConfig.cycle[0], scenarioConfig.cycle[1]);
    const repCount = rndInt(scenarioConfig.repCount[0], scenarioConfig.repCount[1]);
    const targetYear = Math.round(rndInt(scenarioConfig.targetYear[0], scenarioConfig.targetYear[1]) / 10000) * 10000;
    const achievedRatio = rndFloat(scenarioConfig.achieved[0], scenarioConfig.achieved[1]);
    const achievedYTD = Math.round(targetYear * achievedRatio * (new Date().getMonth() + 1) / 12 / 1000) * 1000;
    const avgRepTarget = Math.round(targetYear / repCount / 12);
    const avgRepRevenue = Math.round(avgRepTarget * rndFloat(0.5, 0.9));

    // Conversion-Raten
    const c2dVal = unknownC2D ? null : rndFloat(scenarioConfig.c2d[0], scenarioConfig.c2d[1]);
    const d2pVal = unknownD2P ? null : rndFloat(scenarioConfig.d2p[0], scenarioConfig.d2p[1]);
    const p2dVal = unknownP2D ? null : rndFloat(scenarioConfig.p2d[0], scenarioConfig.p2d[1]);

    const discoveryFromContacts = contacts ? Math.round(contacts * rndFloat(0.08, 0.18)) : rndInt(15, 50);
    const presentations = Math.round(discoveryFromContacts * rndFloat(0.3, 0.65));

    // Akquise-Mix zufällig verteilen (muss 100 ergeben)
    const mixBase = { phone: rndInt(5, 25), email: rndInt(10, 30), linkedin: rndInt(5, 25), xing: rndInt(0, 10),
      referrals: rndInt(5, 20), events: rndInt(0, 15), content: rndInt(0, 10), paid: rndInt(0, 8),
      cold: rndInt(0, 5), partner: rndInt(0, 8) };
    const mixSum = Object.values(mixBase).reduce((a, b) => a + b, 0);
    const scale = (v: number) => Math.round((v / mixSum) * 100);
    const phone = scale(mixBase.phone), email = scale(mixBase.email), linkedin = scale(mixBase.linkedin),
      xing = scale(mixBase.xing), referrals = scale(mixBase.referrals), events = scale(mixBase.events),
      content = scale(mixBase.content), paid = scale(mixBase.paid), cold = scale(mixBase.cold),
      partner = scale(mixBase.partner);
    const mixTotal = phone + email + linkedin + xing + referrals + events + content + paid + cold + partner;
    const other = Math.max(0, 100 - mixTotal);

    // Qualitative Werte szenario-abhängig
    const goodOrBad = (good: string, mid: string, bad: string): string => {
      const weights = { "growth-winner": [0.6, 0.3, 0.1], "stuck-mid": [0.2, 0.5, 0.3],
        "crisis": [0.05, 0.2, 0.75], "blind-spots": [0.2, 0.4, 0.4], "scale-up": [0.3, 0.5, 0.2] }[scenario];
      const r = Math.random();
      return r < weights[0] ? good : r < weights[0] + weights[1] ? mid : bad;
    };

    // Kontakt & Personas
    const personas = [
      { first: "Anna", last: "Weber", role: "VP Sales", company: "Nexus Solutions GmbH", domain: "nexus-solutions.de", industry: "B2B SaaS", model: "b2b-saas" as const },
      { first: "Thomas", last: "Becker", role: "Head of Sales", company: "TechDrive AG", domain: "techdrive.de", industry: "Industrie", model: "b2b-industrial" as const },
      { first: "Sarah", last: "Müller", role: "Geschäftsführer", company: "ConsultGroup GmbH", domain: "consultgroup.de", industry: "Consulting", model: "consulting" as const },
      { first: "Michael", last: "Schmidt", role: "Sales Director", company: "DataForge GmbH", domain: "dataforge.de", industry: "B2B Software", model: "b2b-saas" as const },
      { first: "Julia", last: "Hoffmann", role: "CSO", company: "InnoTrade GmbH", domain: "innotrade.de", industry: "Services", model: "b2b-services" as const },
    ];
    const persona = rnd(personas);

    const companySizes: CompanySizeOption[] = ["11-50", "51-200", "51-200", "201-1000"];

    const unknownDealSize = maybe(0.1);
    const unknownMonthlyRevenueOverride = maybe(0.1);
    const unknownProposalsWithNextStep = maybe(0.1);
    const unknownTimeToFirstMeeting = maybe(0.1);
    const unknownDecisionMakerInFirstMeeting = maybe(0.1);
    const unknownDiscoveryNoShowRate = maybe(0.1);
    const unknownNextStepCoverage = maybe(0.1);
    const unknownQualifiedLeadRate = maybe(0.1);
    const unknownProductiveReps = maybe(0.1);
    const unknownAvgRevenuePerRep = maybe(0.1);
    const unknownTotalRevenueTargetYear = maybe(0.1);
    const unknownRevenueAchievedYTD = maybe(0.1);
    const unknownAvgRevenueTargetPerRep = maybe(0.1);

    setForm({
      // Step 1: Sales Output
      averageDealSize: unknownDealSize ? null : dealSize,
      averageDealSize_unknown: unknownDealSize,
      averageDealSizeRange: null,
      salesCycleMonths: cycle,
      salesCycleRange: null,
      monthlyRevenueOverride: unknownMonthlyRevenueOverride ? null : null,
      monthlyRevenueOverride_unknown: unknownMonthlyRevenueOverride,
      monthlyDeals: deals,
      monthlyContacts: contacts,
      monthlyDiscoveryCalls: discoveryFromContacts,
      monthlyPresentations: presentations,

      // Step 2: Conversion & Funnel
      conversionLeadToDiscovery: c2dVal,
      conversionLeadToDiscoveryRange: unknownC2D ? "not-known" : "not-known",
      convLeadToDiscoveryUnknown: unknownC2D,
      conversionDiscoveryToOffer: d2pVal,
      conversionDiscoveryToOfferRange: unknownD2P ? "not-known" : "not-known",
      convDiscoveryToOfferUnknown: unknownD2P,
      conversionOfferToClose: p2dVal,
      conversionOfferToCloseRange: unknownP2D ? "not-known" : "not-known",
      convOfferToCloseUnknown: unknownP2D,
      monthlyContacts_unknown: unknownContacts,
      monthlyPresentations_unknown: unknownPresentations,
      monthlyDeals_unknown: unknownDeals,

      biggestDropoff: rnd(["lead-to-discovery", "discovery-to-offer", "offer-to-close"] as BiggestDropoffOption[]),
      biggestDropoff_unknown: false,
      touchpointsToClose: rnd(["1-2", "3-5", "6-10", "10+"] as TouchpointsToCloseOption[]),
      stageExitCriteria: rnd(["yes", "partly", "no"]),
      daysFromDiscoveryToProposal: maybe(0.3) ? null : rndInt(3, 21),
      proposalsWithNextStepPercent: maybe(0.2) ? null : rndInt(20, 80),

      // Step 3: Speed & Execution
      responseTime: rnd(["under-1h", "1-3h", "same-day", "1-3days", "1-3days", "more-than-3days"] as ResponseTimeOption[]),
      timeToFirstMeetingDays: rndInt(2, 14),
      followUpDiscipline: goodOrBad("very-structured", "partly-structured", "ad-hoc") as FollowUpDisciplineOption,
      followUpFrequency: rnd(["same-day", "within-2days", "within-week", "irregular"] as FollowUpFrequencyOption[]),
      followUpOwnership: goodOrBad("clear", "partly-clear", "unclear") as OwnershipClarityOption,
      lostLeadsNoResponse: goodOrBad("rarely", "regularly", "often") as LostLeadsOption,
      avgFollowUpsPerLead: unknownFollowUps ? null : rndInt(1, 7),
      avgFollowUpsPerLead_unknown: unknownFollowUps,
      decisionMakerInFirstMeetingPercent: maybe(0.3) ? null : rndInt(15, 65),
      decisionMakerInProcess: rnd(["first-meeting", "mid-process", "late-process", "never"] as DecisionMakerInvolvementOption[]),
      discoveryNoShowRatePercent: maybe(0.25) ? null : rndInt(5, 35),
      discoveryFrameworkStep3: rnd(["yes", "partly", "no"]),

      // Step 4: Pipeline & Governance
      pipelineTransparency: goodOrBad("full", "partial", "minimal") as PipelineTransparencyOption,
      nextStepCoveragePercent: maybe(0.2) ? null : rndInt(20, 85),
      forecastAccuracy: rnd(["very-accurate", "within-20-percent", "within-20-percent", "unreliable"] as ForecastAccuracyOption[]),
      pipelineReviewCadence: goodOrBad("weekly", "monthly", "rarely") as ReviewCadenceOption,
      crmUsage: goodOrBad("fully-used", "partly-used", "barely-used") as CRMUsageOption,
      stalePipelineSharePercent: unknownStale ? null : rndInt(10, 55),
      stalePipelineSharePercent_unknown: unknownStale,
      forecastCoverageRatio: unknownCoverage ? null : rndFloat(1.5, 5.0),
      forecastCoverageRatio_unknown: unknownCoverage,

      // Step 5: Lead Quality & Source
      pipelineSource: rnd(["inbound", "outbound", "mixed", "referrals"] as PipelineSourceOption[]),
      leadQuality: goodOrBad("very-high", "mixed", "weak") as LeadQualityOption,
      icpDefined: rnd(["yes", "partly", "no"]),
      icpClarity: goodOrBad("very-clear", "mostly-clear", "vague") as IcpClarityOption,
      leadScoringMaturity: rnd(["formal", "informal", "none"] as LeadScoringOption[]),
      qualifiedLeadRatePercent: unknownQualifiedLeadRate ? null : rndInt(15, 65),
      qualifiedLeadRatePercent_unknown: unknownQualifiedLeadRate,
      marketPresence: rnd(["strong", "moderate", "weak"] as MarketPresenceOption[]),

      // Step 5b: Akquise-Mix
      acquisitionPhone: phone, acquisitionEmail: email, acquisitionLinkedIn: linkedin,
      acquisitionXing: xing, acquisitionReferrals: referrals, acquisitionEvents: events,
      acquisitionContentMarketing: content, acquisitionPaidAds: paid, acquisitionColdVisits: cold,
      acquisitionPartner: partner, acquisitionOther: other,

      // Step 6: Tools & Systems
      crmAvailable: rnd(["yes", "yes", "partly", "no"]),
      crmName: rnd(["HubSpot", "Salesforce", "Pipedrive", "Microsoft Dynamics", "Zoho CRM", ""]),
      marketingAutomation: rnd(["yes", "partly", "no"]),
      leadRouting: rnd(["yes", "partly", "no"]),
      salesPlaybookAvailable: goodOrBad("yes", "partly", "no") as YesNoUnknown,

      // Step 7: Ziel & Engpass
      salesGoal: rnd(["revenue-growth", "more-qualified-leads", "higher-conversion", "better-forecast", "more-pipeline"] as GoalOption[]),
      primaryPain: rnd(["low-conversion", "too-few-leads", "long-sales-cycle", "low-win-rate", "missing-pipeline-structure"] as PainOption[]),

      // Step 8: Unternehmenskontext
      companyName: persona.company,
      companyDomain: persona.domain,
      industry: persona.industry,
      companySize: rnd(companySizes),
      teamSize: repCount,
      businessModel: persona.model,

      // Step 9: Ansprechpartner
      contactFirstName: persona.first,
      contactLastName: persona.last,
      contactRole: persona.role,
      contactEmail: `${persona.first.toLowerCase()}.${persona.last.toLowerCase()}@${persona.domain}`,
      contactPhone: `+49 ${rndInt(100, 999)} ${rndInt(1000000, 9999999)}`,
      wantsEmailResults: true,

      // Sales Rep Performance
      productiveReps: unknownProductiveReps ? null : repCount,
      productiveReps_unknown: unknownProductiveReps,
      avgRevenuePerRepVisibility: rnd(["exact", "estimated", "not-known"] as RevenueVisibilityOption[]),
      avgRevenuePerRep: unknownAvgRevenuePerRep ? null : avgRepRevenue,
      avgRevenuePerRep_unknown: unknownAvgRevenuePerRep,
      totalRevenueTargetYear: unknownTotalRevenueTargetYear ? null : targetYear,
      totalRevenueTargetYear_unknown: unknownTotalRevenueTargetYear,
      revenueAchievedYTD: unknownRevenueAchievedYTD ? null : achievedYTD,
      revenueAchievedYTD_unknown: unknownRevenueAchievedYTD,
      revenueTargetBrokenDownToReps: rnd(["yes", "partial", "no"] as RevenueTargetBreakdownOption[]),
      repsHaveRevenueTarget: rnd(["yes", "partly", "no"]),
      avgRevenueTargetPerRep: unknownAvgRevenueTargetPerRep ? null : avgRepTarget,
      avgRevenueTargetPerRep_unknown: unknownAvgRevenueTargetPerRep,
      repsOnTrackToTarget: goodOrBad("almost-all", "more-than-half", "less-than-half") as RepsOnTrackOption,
      repPerformanceVariance: goodOrBad("low", "moderate", "high") as PerformanceVarianceOption,
      repsWithEnoughPipeline: goodOrBad("almost-all", "more-than-half", "less-than-half") as RepsWithEnoughPipelineOption,
      pipelineConfidence: goodOrBad("very-high", "rather-high", "rather-low") as PipelineConfidenceOption,
      repReviewCadence: rnd(["weekly", "biweekly", "monthly", "rarely"] as RepReviewCadenceOption[]),
    } as AuditFormData);

    console.info(`[TestData] Szenario: ${scenarioConfig.label}`, {
      unknownC2D, unknownD2P, unknownP2D, unknownContacts, unknownStale, unknownCoverage,
    });

    // JUMP TO LAST STEP
    setCurrentStep(10);
  }

  const currentStepMeta = STEPS[currentStep];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
      {/* HERO */}
      <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 max-w-5xl">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-primary/10">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold leading-tight">Premium Sales Audit</h1>
                <p className="text-xs text-muted-foreground">
                  Schritt {currentStep + 1} von {STEPS.length} &middot; {overallCompletion}% vollständig
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-xs text-muted-foreground mb-1 hidden md:block">Readiness Score</div>
                <div className="text-2xl font-bold text-primary">{normalized.dynamicSignals.readinessScore}</div>
              </div>
            </div>
          </div>

          {/* Mobile: Dropdown-Select für alle Schritte – im sticky Header */}
          <div className="md:hidden mb-2">
            <select
              value={currentStep}
              onChange={(e) => setCurrentStep(Number(e.target.value))}
              className="w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm font-medium text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {STEPS.map((step, idx) => {
                const done = idx < currentStep;
                const prefix = done ? `✓ ` : `${idx + 1}. `;
                return (
                  <option key={step.id} value={idx}>
                    {prefix}{step.title}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-muted rounded-full h-1.5">
            <div
              className="bg-primary h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${overallCompletion}%` }}
            />
          </div>
          {/* Auto-Save Hinweis */}
          <p className="text-[10px] text-muted-foreground/60 mt-1.5 text-right">
            ✓ Fortschritt wird automatisch gespeichert
          </p>
        </div>
      </div>

      {/* STEP NAV */}
      <div className="container mx-auto px-4 py-8 max-w-5xl">

        {/* Desktop: alle Schritte als klickbare Buttons */}
        <div className="hidden md:flex flex-wrap gap-1.5 mb-8">
          {STEPS.map((step, idx) => {
            const complete = getStepCompletion(form, idx);
            const active = idx === currentStep;
            const done = idx < currentStep;
            return (
              <button
                key={step.id}
                type="button"
                onClick={() => setCurrentStep(idx)}
                className={cn(
                  "relative flex-shrink-0 rounded-xl border px-3 py-2 text-left transition-all overflow-hidden",
                  "min-w-[80px] max-w-[100px]",
                  active
                    ? "border-primary bg-primary/5 shadow-sm ring-1 ring-primary/20"
                    : done
                    ? "border-green-500/30 bg-green-500/5 hover:border-green-500/50"
                    : "border-border bg-card hover:border-primary/30"
                )}
              >
                <div className="text-[10px] font-semibold text-muted-foreground mb-0.5">Schritt {idx + 1}</div>
                <div className="text-[10px] leading-tight truncate font-medium"
                     style={{ color: active ? "var(--primary)" : done ? "rgb(22 163 74)" : "var(--foreground)" }}
                >
                  {step.title}
                </div>
                {done && (
                  <CheckCircle2 className="w-3 h-3 text-green-600 absolute top-1.5 right-1.5" />
                )}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-muted">
                  <div
                    className={cn(
                      "h-0.5 transition-all duration-300",
                      active ? "bg-primary" : done ? "bg-green-500" : "bg-muted-foreground/20"
                    )}
                    style={{ width: `${complete}%` }}
                  />
                </div>
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {showErrors && currentErrors.length > 0 ? (
            <div className="rounded-2xl border-2 border-destructive/20 bg-destructive/5 p-6">
              <p className="font-semibold text-destructive mb-2">Bitte prüfen:</p>
              <ul className="space-y-1">
                {currentErrors.map((error, idx) => (
                  <li key={idx} className="text-sm text-destructive">• {error}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Current Step Title */}
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-2">{currentStepMeta.title}</h2>
            <p className="text-muted-foreground">{currentStepMeta.subtitle}</p>
          </div>

          {/* STEP CONTENT */}
          <div className="bg-card rounded-3xl border p-8 shadow-sm">
            {/* STEP 1: SALES OUTPUT */}
            {currentStep === 0 && (
              <SectionBox
                title="Sales Output"
                subtitle="Diese Zahlen sind das Fundament Ihrer späteren Umsatzlogik"
              >
                <div className="space-y-4">
                  {/* Kompakte 2-Spalten-Layouts für Hauptfelder */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Deal Size */}
                    <div>
                      <FieldLabel required tooltip="Der durchschnittliche Wert eines abgeschlossenen Deals in Euro. Wählen Sie einen Bereich oder geben Sie einen exakten Wert ein.">Durchschnittliche Deal-Größe</FieldLabel>
                      <div className="flex gap-2">
                        <select
                          value={form.averageDealSizeRange || ""}
                          disabled={!!form.averageDealSize_unknown}
                          onChange={(e) => {
                            update("averageDealSizeRange", e.target.value || null);
                            update("averageDealSize", null);
                          }}
                          className={`flex-1 rounded-xl border px-3 py-2 text-sm outline-none focus:border-primary ${
                            form.averageDealSize_unknown ? "bg-amber-50 border-amber-200 text-amber-400 cursor-not-allowed" : "bg-card"
                          }`}
                        >
                          <option value="">{form.averageDealSize_unknown ? "Nicht bekannt" : "Bereich"}</option>
                          {!form.averageDealSize_unknown && DEAL_SIZE_RANGES.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          disabled={!!form.averageDealSize_unknown}
                          value={form.averageDealSize === null ? "" : form.averageDealSize}
                          onChange={(e) => {
                            const val = e.target.value === "" ? null : Number(e.target.value);
                            update("averageDealSize", val);
                            update("averageDealSizeRange", null);
                            update("averageDealSize_unknown", false);
                          }}
                          placeholder={form.averageDealSize_unknown ? "Nicht bekannt" : "Exakt"}
                          className={`w-24 rounded-xl border px-3 py-2 text-sm outline-none focus:border-primary ${
                            form.averageDealSize_unknown ? "bg-amber-50 border-amber-200 text-amber-400 cursor-not-allowed" : "bg-card"
                          }`}
                        />
                      </div>
                      <label className="flex items-center gap-2 mt-2 cursor-pointer select-none group w-fit">
                        <div
                          onClick={() => {
                            const next = !form.averageDealSize_unknown;
                            update("averageDealSize_unknown", next);
                            if (next) { update("averageDealSize", null); update("averageDealSizeRange", null); }
                          }}
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer ${
                            form.averageDealSize_unknown ? "bg-amber-500 border-amber-500" : "border-slate-300 bg-white group-hover:border-amber-400"
                          }`}
                        >
                          {!!form.averageDealSize_unknown && (
                            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                              <path d="M10 3L5 8.5 2 5.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                        <span className={`text-xs transition-colors ${
                          form.averageDealSize_unknown ? "text-amber-600 font-medium" : "text-muted-foreground group-hover:text-amber-600"
                        }`}>
                          Nicht bekannt
                        </span>
                      </label>
                    </div>

                    {/* Sales Cycle */}
                    <div>
                      <FieldLabel required tooltip="Die durchschnittliche Zeit vom ersten Kontakt bis zum Vertragsabschluss in Monaten.">Sales Cycle Dauer</FieldLabel>
                      <div className="flex gap-2">
                        <select
                          value={form.salesCycleRange || ""}
                          disabled={!!form.salesCycleMonths_unknown}
                          onChange={(e) => {
                            update("salesCycleRange", e.target.value || null);
                            update("salesCycleMonths", null);
                          }}
                          className={`flex-1 rounded-xl border px-3 py-2 text-sm outline-none focus:border-primary ${
                            form.salesCycleMonths_unknown ? "bg-amber-50 border-amber-200 text-amber-400 cursor-not-allowed" : "bg-card"
                          }`}
                        >
                          <option value="">{form.salesCycleMonths_unknown ? "Nicht bekannt" : "Bereich"}</option>
                          {!form.salesCycleMonths_unknown && SALES_CYCLE_RANGES.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          disabled={!!form.salesCycleMonths_unknown}
                          value={form.salesCycleMonths === null ? "" : form.salesCycleMonths}
                          onChange={(e) => {
                            const val = e.target.value === "" ? null : Number(e.target.value);
                            update("salesCycleMonths", val);
                            update("salesCycleRange", null);
                            update("salesCycleMonths_unknown", false);
                          }}
                          placeholder={form.salesCycleMonths_unknown ? "Nicht bekannt" : "Monate"}
                          className={`w-28 rounded-xl border px-3 py-2 text-sm outline-none focus:border-primary ${
                            form.salesCycleMonths_unknown ? "bg-amber-50 border-amber-200 text-amber-400 cursor-not-allowed" : "bg-card"
                          }`}
                        />
                      </div>
                      <label className="flex items-center gap-2 mt-2 cursor-pointer select-none group w-fit">
                        <div
                          onClick={() => {
                            const next = !form.salesCycleMonths_unknown;
                            update("salesCycleMonths_unknown", next);
                            if (next) { update("salesCycleMonths", null); update("salesCycleRange", null); }
                          }}
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer ${
                            form.salesCycleMonths_unknown ? "bg-amber-500 border-amber-500" : "border-slate-300 bg-white group-hover:border-amber-400"
                          }`}
                        >
                          {!!form.salesCycleMonths_unknown && (
                            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                              <path d="M10 3L5 8.5 2 5.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                        <span className={`text-xs transition-colors ${
                          form.salesCycleMonths_unknown ? "text-amber-600 font-medium" : "text-muted-foreground group-hover:text-amber-600"
                        }`}>
                          Nicht bekannt
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Kompakte 3-Spalten für Zahlen */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <FieldLabel required tooltip="Wie viele Deals schließen Sie durchschnittlich pro Monat ab?">Abschlüsse/Monat</FieldLabel>
                      <NumberInput
                        value={form.monthlyDeals}
                        onChange={(v) => { update("monthlyDeals", v); if (v !== null) update("monthlyDeals_unknown", false); }}
                        placeholder="z. B. 4"
                        unknownId="monthlyDeals_unknown"
                        unknownChecked={!!form.monthlyDeals_unknown}
                        onUnknownChange={(checked) => { update("monthlyDeals_unknown", checked); if (checked) update("monthlyDeals", null); }}
                      />
                    </div>

                    <div>
                      <FieldLabel required tooltip="Anzahl der qualifizierten Leads oder Kontakte, die monatlich in Ihren Sales-Funnel eintreten.">Kontakte/Monat</FieldLabel>
                      <NumberInput
                        value={form.monthlyContacts}
                        onChange={(v) => { update("monthlyContacts", v); if (v !== null) update("monthlyContacts_unknown", false); }}
                        placeholder="z. B. 200"
                        unknownId="monthlyContacts_unknown"
                        unknownChecked={!!form.monthlyContacts_unknown}
                        onUnknownChange={(checked) => { update("monthlyContacts_unknown", checked); if (checked) update("monthlyContacts", null); }}
                      />
                    </div>

                    <div>
                      <FieldLabel required tooltip="Anzahl der Produktpräsentationen, Demos oder Angebote, die Sie monatlich durchführen.">Präsentationen/Monat</FieldLabel>
                      <NumberInput
                        value={form.monthlyPresentations}
                        onChange={(v) => { update("monthlyPresentations", v); if (v !== null) update("monthlyPresentations_unknown", false); }}
                        placeholder="z. B. 8"
                        unknownId="monthlyPresentations_unknown"
                        unknownChecked={!!form.monthlyPresentations_unknown}
                        onUnknownChange={(checked) => { update("monthlyPresentations_unknown", checked); if (checked) update("monthlyPresentations", null); }}
                      />
                    </div>
                  </div>

                  {/* Optional: Discovery Calls */}
                  <div>
                    <FieldLabel tooltip="Anzahl der Erstgespräche oder Qualifizierungsgespräche mit potenziellen Kunden pro Monat.">Discovery Calls pro Monat (optional)</FieldLabel>
                    <NumberInput
                      value={form.monthlyDiscoveryCalls}
                      onChange={(v) => { update("monthlyDiscoveryCalls", v); if (v !== null) update("monthlyDiscoveryCalls_unknown", false); }}
                      placeholder="z. B. 20"
                      unknownId="monthlyDiscoveryCalls_unknown"
                      unknownChecked={!!form.monthlyDiscoveryCalls_unknown}
                      onUnknownChange={(checked) => { update("monthlyDiscoveryCalls_unknown", checked); if (checked) update("monthlyDiscoveryCalls", null); }}
                    />
                  </div>

                  {/* Optional: Monthly Revenue Override */}
                  <div>
                    <FieldLabel tooltip="Ihr tatsächlicher monatlicher Umsatz. Wenn angegeben, überschreibt dieser Wert die automatische Berechnung.">Monatlicher Umsatz aktuell (optional)</FieldLabel>
                    <NumberInput
                      value={form.monthlyRevenueOverride}
                      onChange={(v) => update("monthlyRevenueOverride", v)}
                      placeholder="Überschreibt Berechnung"
                      suffix="€"
                    />
                    <FieldHint>
                      Wenn angegeben, überschreibt dieser Wert die Berechnung aus Deal-Größe × Abschlüssen
                    </FieldHint>
                  </div>
                </div>
              </SectionBox>
            )}

            {/* STEP 2: CONVERSION & FUNNEL */}
            {currentStep === 1 && (() => {
              // Konfidenz-Score: Wie viele der 3 Conversion-Rates sind bekannt?
              const convKnownCount = [
                !form.convLeadToDiscoveryUnknown && (form.conversionLeadToDiscovery !== null || form.conversionLeadToDiscoveryRange !== "not-known"),
                !form.convDiscoveryToOfferUnknown && (form.conversionDiscoveryToOffer !== null || form.conversionDiscoveryToOfferRange !== "not-known"),
                !form.convOfferToCloseUnknown && (form.conversionOfferToClose !== null || form.conversionOfferToCloseRange !== "not-known"),
              ].filter(Boolean).length;
              const convConfidenceColor = convKnownCount === 3 ? "text-green-600 bg-green-50 border-green-200" : convKnownCount >= 1 ? "text-amber-600 bg-amber-50 border-amber-200" : "text-red-600 bg-red-50 border-red-200";
              const convConfidenceLabel = convKnownCount === 3 ? "Vollständig – höchste Diagnosequalität" : convKnownCount >= 1 ? `${convKnownCount}/3 bekannt – Branchenschnitt wird ergänzt` : "Keine Daten – Branchenschnitt wird verwendet";

              // Plausibilitäts-Check: Deals > theoretische Deals?
              const c2d = form.conversionLeadToDiscovery !== null ? form.conversionLeadToDiscovery / 100 : null;
              const d2o = form.conversionDiscoveryToOffer !== null ? form.conversionDiscoveryToOffer / 100 : null;
              const o2c = form.conversionOfferToClose !== null ? form.conversionOfferToClose / 100 : null;
              const contacts = form.monthlyContacts;
              const actualDeals = form.monthlyDeals;
              let plausibilityWarning: string | null = null;
              if (c2d !== null && d2o !== null && o2c !== null && contacts !== null && contacts > 0 && actualDeals !== null) {
                const theoreticalDeals = contacts * c2d * d2o * o2c;
                if (actualDeals > theoreticalDeals * 1.2) {
                  plausibilityWarning = `⚠️ Ihre ${actualDeals} Abschlüsse übersteigen den theoretischen Wert von ${theoreticalDeals.toFixed(1)} aus ${contacts} Kontakten × ${(c2d*100).toFixed(0)}% × ${(d2o*100).toFixed(0)}% × ${(o2c*100).toFixed(0)}%. Bitte prüfen Sie Ihre Eingaben.`;
                }
              }

              return (
              <SectionBox
                title="Conversion & Funnel"
                subtitle="Hier trennen wir Ursache und Symptom"
              >
                <div className="space-y-6">
                  {/* Konfidenz-Banner */}
                  <div className={`flex items-center gap-2 p-3 rounded-lg border text-sm font-medium ${convConfidenceColor}`}>
                    <span>{convKnownCount === 3 ? "✓" : convKnownCount >= 1 ? "◑" : "○"}</span>
                    <span>Conversion-Konfidenz: {convConfidenceLabel}</span>
                  </div>

                  {/* Plausibilitäts-Warnung */}
                  {plausibilityWarning && (
                    <div className="p-3 rounded-lg border border-orange-200 bg-orange-50 text-sm text-orange-700">
                      {plausibilityWarning}
                    </div>
                  )}

                  {/* Conversion Lead → Discovery */}
                  <div>
                    <FieldLabel tooltip="Wie viel Prozent Ihrer Leads führen zu einem Discovery Call oder Erstgespräch?">Conversion Lead → Discovery</FieldLabel>
                    <NumberInput
                      value={form.conversionLeadToDiscovery}
                      onChange={(v) => { update("conversionLeadToDiscovery", v); if (v !== null) update("convLeadToDiscoveryUnknown", false); }}
                      placeholder="z. B. 28"
                      min={0}
                      max={100}
                      suffix="%"
                      unknownId="convLeadToDiscovery_unknown"
                      unknownChecked={!!form.convLeadToDiscoveryUnknown}
                      onUnknownChange={(checked) => {
                        update("convLeadToDiscoveryUnknown", checked);
                        if (checked) { update("conversionLeadToDiscovery", null); update("conversionLeadToDiscoveryRange", "not-known"); }
                      }}
                    />
                    {!form.convLeadToDiscoveryUnknown && form.conversionLeadToDiscovery !== null ? (
                      <div className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded px-2 py-0.5">
                        ✓ Ihr Wert
                      </div>
                    ) : form.convLeadToDiscoveryUnknown ? (
                      <div className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-0.5">
                        ⟳ Branchenschnitt: 30% (Fallback)
                      </div>
                    ) : null}
                  </div>

                  {/* Conversion Discovery → Offer */}
                  <div>
                    <FieldLabel tooltip="Wie viel Prozent Ihrer Discovery Calls führen zu einer Präsentation oder einem Angebot?">Conversion Discovery → Angebot</FieldLabel>
                    <NumberInput
                      value={form.conversionDiscoveryToOffer}
                      onChange={(v) => { update("conversionDiscoveryToOffer", v); if (v !== null) update("convDiscoveryToOfferUnknown", false); }}
                      placeholder="z. B. 35"
                      min={0}
                      max={100}
                      suffix="%"
                      unknownId="convDiscoveryToOffer_unknown"
                      unknownChecked={!!form.convDiscoveryToOfferUnknown}
                      onUnknownChange={(checked) => {
                        update("convDiscoveryToOfferUnknown", checked);
                        if (checked) { update("conversionDiscoveryToOffer", null); update("conversionDiscoveryToOfferRange", "not-known"); }
                      }}
                    />
                    {!form.convDiscoveryToOfferUnknown && form.conversionDiscoveryToOffer !== null ? (
                      <div className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded px-2 py-0.5">
                        ✓ Ihr Wert
                      </div>
                    ) : form.convDiscoveryToOfferUnknown ? (
                      <div className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-0.5">
                        ⟳ Branchenschnitt: 25% (Fallback)
                      </div>
                    ) : null}
                  </div>

                  {/* Conversion Offer → Close */}
                  <div>
                    <FieldLabel tooltip="Wie viel Prozent Ihrer Angebote oder Präsentationen führen zu einem Vertragsabschluss?">Conversion Angebot → Abschluss</FieldLabel>
                    <NumberInput
                      value={form.conversionOfferToClose}
                      onChange={(v) => { update("conversionOfferToClose", v); if (v !== null) update("convOfferToCloseUnknown", false); }}
                      placeholder="z. B. 27"
                      min={0}
                      max={100}
                      suffix="%"
                      unknownId="convOfferToClose_unknown"
                      unknownChecked={!!form.convOfferToCloseUnknown}
                      onUnknownChange={(checked) => {
                        update("convOfferToCloseUnknown", checked);
                        if (checked) { update("conversionOfferToClose", null); update("conversionOfferToCloseRange", "not-known"); }
                      }}
                    />
                    {!form.convOfferToCloseUnknown && form.conversionOfferToClose !== null ? (
                      <div className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded px-2 py-0.5">
                        ✓ Ihr Wert
                      </div>
                    ) : form.convOfferToCloseUnknown ? (
                      <div className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-0.5">
                        ⟳ Branchenschnitt: 25% (Fallback)
                      </div>
                    ) : null}
                  </div>

                  {/* Biggest Dropoff */}
                  <div>
                    <FieldLabel tooltip="An welcher Stelle im Verkaufsprozess verlieren Sie die meisten Opportunities?">Wo ist der größte Drop-off im Funnel?</FieldLabel>
                    <SelectInput
                      value={form.biggestDropoff}
                      onChange={(v) => { update("biggestDropoff", v); update("biggestDropoff_unknown", false); }}
                      options={BIGGEST_DROPOFF_OPTIONS}
                      unknownId="biggestDropoff_unknown"
                      unknownChecked={!!form.biggestDropoff_unknown}
                      onUnknownChange={(checked) => { update("biggestDropoff_unknown", checked); }}
                    />
                  </div>

                  {/* Touchpoints */}
                  <div>
                    <FieldLabel tooltip="Durchschnittliche Anzahl der Kontaktpunkte (E-Mails, Calls, Meetings) vom ersten Kontakt bis zum Vertragsabschluss.">Wie viele Touchpoints braucht es bis zum Abschluss?</FieldLabel>
                    <SelectInput
                      value={form.touchpointsToClose}
                      onChange={(v) => { update("touchpointsToClose", v); update("touchpointsToClose_unknown", false); }}
                      options={TOUCHPOINT_OPTIONS}
                      unknownId="touchpointsToClose_unknown"
                      unknownChecked={!!form.touchpointsToClose_unknown}
                      onUnknownChange={(checked) => { update("touchpointsToClose_unknown", checked); }}
                    />
                  </div>

                  {/* Stage Exit Criteria */}
                  <div>
                    <FieldLabel tooltip="Sind für jede Verkaufsphase klare Kriterien definiert, wann ein Lead zur nächsten Phase übergehen kann?">Gibt es klare Exit-Kriterien pro Stage?</FieldLabel>
                    <SelectInput
                      value={form.stageExitCriteria}
                      onChange={(v) => { update("stageExitCriteria", v); update("stageExitCriteria_unknown", false); }}
                      options={YES_NO_UNKNOWN_OPTIONS}
                      unknownId="stageExitCriteria_unknown"
                      unknownChecked={!!form.stageExitCriteria_unknown}
                      onUnknownChange={(checked) => { update("stageExitCriteria_unknown", checked); }}
                    />
                  </div>

                  {/* Days from Discovery to Proposal (NEW) */}
                  <div>
                    <FieldLabel tooltip="Durchschnittliche Anzahl der Tage vom Discovery Call bis zur Angebotspräsentation.">Wie viele Tage vergehen durchschnittlich vom Discovery bis zum Angebot?</FieldLabel>
                    <NumberInput
                      value={form.daysFromDiscoveryToProposal}
                      onChange={(v) => { update("daysFromDiscoveryToProposal", v); if (v !== null) update("daysFromDiscoveryToProposal_unknown", false); }}
                      placeholder="z. B. 7"
                      suffix="Tage"
                      min={0}
                      unknownId="daysFromDiscoveryToProposal_unknown"
                      unknownChecked={!!form.daysFromDiscoveryToProposal_unknown}
                      onUnknownChange={(checked) => { update("daysFromDiscoveryToProposal_unknown", checked); if (checked) update("daysFromDiscoveryToProposal", null); }}
                    />
                    <FieldHint>Optional – hilft, Verzögerungen in der Angebotsphase zu identifizieren.</FieldHint>
                  </div>

                  {/* Proposals with next step (NEW) */}
                  <div>
                    <FieldLabel tooltip="Anteil der Angebote, bei denen direkt ein konkreter Next Step vereinbart wird.">Bei wie viel % der Angebote wird sofort ein Next Step vereinbart?</FieldLabel>
                    <NumberInput
                      value={form.proposalsWithNextStepPercent}
                      onChange={(v) => update("proposalsWithNextStepPercent", v)}
                      placeholder="z. B. 50"
                      suffix="%"
                      min={0}
                      max={100}
                    />
                    <FieldHint>Optional – ein niedriger Wert zeigt fehlende Verbindlichkeit im Closing.</FieldHint>
                  </div>
                </div>
              </SectionBox>
              );
            })()}

            {/* STEP 3: SPEED & EXECUTION */}
            {currentStep === 2 && (
              <SectionBox
                title="Speed & Execution"
                subtitle="Geschwindigkeit ist ein eigenständiger Hebel"
              >
                <div className="space-y-6">
                  {/* Response Time */}
                  <div>
                    <FieldLabel tooltip="Wie schnell reagiert Ihr Team durchschnittlich auf neue eingehende Leads?">Durchschnittliche Reaktionszeit auf neue Leads</FieldLabel>
                    <SelectInput
                      value={form.responseTime}
                      onChange={(v) => { update("responseTime", v); update("responseTime_unknown", false); }}
                      options={RESPONSE_OPTIONS}
                      unknownId="responseTime_unknown"
                      unknownChecked={!!form.responseTime_unknown}
                      onUnknownChange={(checked) => { update("responseTime_unknown", checked); }}
                    />
                  </div>

                  {/* Time to First Meeting */}
                  <div>
                    <FieldLabel tooltip="Durchschnittliche Anzahl der Tage vom ersten Kontakt bis zum ersten Gesprächstermin.">Zeit bis zum ersten Termin (in Tagen)</FieldLabel>
                    <NumberInput
                      value={form.timeToFirstMeetingDays}
                      onChange={(v) => update("timeToFirstMeetingDays", v)}
                      placeholder="z. B. 5"
                      suffix="Tage"
                    />
                  </div>

                  {/* Follow-up Discipline */}
                  <div>
                    <FieldLabel tooltip="Gibt es einen systematischen Prozess für die Nachverfolgung von Leads und Opportunities?">Wie strukturiert ist Ihr Follow-up?</FieldLabel>
                    <SelectInput
                      value={form.followUpDiscipline}
                      onChange={(v) => { update("followUpDiscipline", v); update("followUpDiscipline_unknown", false); }}
                      options={FOLLOW_UP_OPTIONS}
                      unknownId="followUpDiscipline_unknown"
                      unknownChecked={!!form.followUpDiscipline_unknown}
                      onUnknownChange={(checked) => { update("followUpDiscipline_unknown", checked); }}
                    />
                  </div>

                  {/* Follow-up Frequency */}
                  <div>
                    <FieldLabel tooltip="Zeitspanne zwischen dem Discovery Call und der ersten Nachverfolgung.">Wie schnell erfolgt das erste Follow-up nach Discovery?</FieldLabel>
                    <SelectInput
                      value={form.followUpFrequency}
                      onChange={(v) => { update("followUpFrequency", v); update("followUpFrequency_unknown", false); }}
                      options={FOLLOW_UP_FREQUENCY_OPTIONS}
                      unknownId="followUpFrequency_unknown"
                      unknownChecked={!!form.followUpFrequency_unknown}
                      onUnknownChange={(checked) => { update("followUpFrequency_unknown", checked); }}
                    />
                  </div>

                  {/* Follow-up Ownership */}
                  <div>
                    <FieldLabel tooltip="Ist eindeutig definiert, wer für die Nachverfolgung von Leads verantwortlich ist?">Ist die Verantwortung für Follow-up klar geregelt?</FieldLabel>
                    <SelectInput
                      value={form.followUpOwnership}
                      onChange={(v) => { update("followUpOwnership", v); update("followUpOwnership_unknown", false); }}
                      options={OWNERSHIP_OPTIONS}
                      unknownId="followUpOwnership_unknown"
                      unknownChecked={!!form.followUpOwnership_unknown}
                      onUnknownChange={(checked) => { update("followUpOwnership_unknown", checked); }}
                    />
                  </div>

                  {/* Lost Leads */}
                  <div>
                    <FieldLabel tooltip="Häufigkeit, mit der potenzielle Kunden verloren gehen, weil keine rechtzeitige Reaktion erfolgte.">Wie oft gehen Leads verloren, weil niemand reagiert hat?</FieldLabel>
                    <SelectInput
                      value={form.lostLeadsNoResponse}
                      onChange={(v) => { update("lostLeadsNoResponse", v); update("lostLeadsNoResponse_unknown", false); }}
                      options={LOST_LEADS_OPTIONS}
                      unknownId="lostLeadsNoResponse_unknown"
                      unknownChecked={!!form.lostLeadsNoResponse_unknown}
                      onUnknownChange={(checked) => { update("lostLeadsNoResponse_unknown", checked); }}
                    />
                  </div>

                  {/* Avg Follow-ups per Lead (NEW) */}
                  <div>
                    <FieldLabel tooltip="Durchschnittliche Anzahl an Follow-up-Versuchen pro Lead, bevor er als verloren markiert wird.">Wie viele Follow-up-Versuche unternimmt ein Rep pro Lead durchschnittlich?</FieldLabel>
                    <NumberInput
                      value={form.avgFollowUpsPerLead}
                      onChange={(v) => { update("avgFollowUpsPerLead", v); if (v !== null) update("avgFollowUpsPerLead_unknown", false); }}
                      placeholder="z. B. 4"
                      min={0}
                      unknownId="avgFollowUpsPerLead_unknown"
                      unknownChecked={!!form.avgFollowUpsPerLead_unknown}
                      onUnknownChange={(checked) => { update("avgFollowUpsPerLead_unknown", checked); if (checked) update("avgFollowUpsPerLead", null); }}
                    />
                    <FieldHint>Optional – unter 3 Follow-ups gilt als kritisches Signal.</FieldHint>
                  </div>

                  {/* Decision Maker in First Meeting % (NEW) */}
                  <div>
                    <FieldLabel tooltip="Prozentualer Anteil der Discovery Calls, in denen der Entscheider von Anfang an dabei ist.">Bei wie viel % der Discovery Calls ist der Entscheider direkt dabei?</FieldLabel>
                    <NumberInput
                      value={form.decisionMakerInFirstMeetingPercent}
                      onChange={(v) => update("decisionMakerInFirstMeetingPercent", v)}
                      placeholder="z. B. 40"
                      suffix="%"
                      min={0}
                      max={100}
                    />
                    <FieldHint>Optional – ein niedriger Wert verlängert oft den Sales Cycle erheblich.</FieldHint>
                  </div>

                  {/* Decision Maker Involvement (NEW) */}
                  <div>
                    <FieldLabel tooltip="Ab welchem Zeitpunkt im Sales Prozess wird der Entscheider typischerweise einbezogen?">Wann wird der Entscheider typischerweise in den Prozess einbezogen?</FieldLabel>
                    <SelectInput
                      value={form.decisionMakerInProcess}
                      onChange={(v) => { update("decisionMakerInProcess", v); update("decisionMakerInProcess_unknown", false); }}
                      options={DECISION_MAKER_INVOLVEMENT_OPTIONS}
                      unknownId="decisionMakerInProcess_unknown"
                      unknownChecked={!!form.decisionMakerInProcess_unknown}
                      onUnknownChange={(checked) => { update("decisionMakerInProcess_unknown", checked); }}
                    />
                  </div>

                  {/* Discovery No-Show Rate (NEW) */}
                  <div>
                    <FieldLabel tooltip="Anteil der vereinbarten Discovery Calls, bei denen der Interessent nicht erscheint.">Wie hoch ist die No-Show-Rate bei Discovery Calls?</FieldLabel>
                    <NumberInput
                      value={form.discoveryNoShowRatePercent}
                      onChange={(v) => { update("discoveryNoShowRatePercent", v); if (v !== null) update("discoveryNoShowRatePercent_unknown", false); }}
                      placeholder="z. B. 15"
                      suffix="%"
                      min={0}
                      max={100}
                      unknownId="discoveryNoShowRatePercent_unknown"
                      unknownChecked={!!form.discoveryNoShowRatePercent_unknown}
                      onUnknownChange={(checked) => { update("discoveryNoShowRatePercent_unknown", checked); if (checked) update("discoveryNoShowRatePercent", null); }}
                    />
                    <FieldHint>Optional – über 20% ist ein starkes Signal für schlechte Qualifizierung oder fehlende Verbindlichkeit.</FieldHint>
                  </div>

                  {/* Discovery Framework Step 3 (NEW) */}
                  <div>
                    <FieldLabel tooltip="Nutzen Ihre Reps ein strukturiertes Discovery-Framework (z. B. SPIN, MEDDIC, SPICED)?">Verwenden Ihre Reps ein strukturiertes Discovery-Framework?</FieldLabel>
                    <SelectInput
                      value={form.discoveryFrameworkStep3}
                      onChange={(v) => { update("discoveryFrameworkStep3", v); update("discoveryFrameworkStep3_unknown", false); }}
                      options={YES_NO_UNKNOWN_OPTIONS}
                      unknownId="discoveryFrameworkStep3_unknown"
                      unknownChecked={!!form.discoveryFrameworkStep3_unknown}
                      onUnknownChange={(checked) => { update("discoveryFrameworkStep3_unknown", checked); }}
                    />
                  </div>
                </div>
              </SectionBox>
            )}

            {/* STEP 4: PIPELINE & GOVERNANCE */}
            {currentStep === 3 && (
              <SectionBox
                title="Pipeline & Governance"
                subtitle="Ohne Struktur keine Steuerung"
              >
                <div className="space-y-6">
                  {/* Pipeline Transparency */}
                  <div>
                    <FieldLabel tooltip="Können Sie jederzeit den Status aller Opportunities einsehen und verstehen, wo sie im Prozess stehen?">Wie transparent ist Ihre Pipeline aktuell?</FieldLabel>
                    <SelectInput
                      value={form.pipelineTransparency}
                      onChange={(v) => { update("pipelineTransparency", v); update("pipelineTransparency_unknown", false); }}
                      options={PIPELINE_OPTIONS}
                      unknownId="pipelineTransparency_unknown"
                      unknownChecked={!!form.pipelineTransparency_unknown}
                      onUnknownChange={(checked) => { update("pipelineTransparency_unknown", checked); }}
                    />
                  </div>

                  {/* Next Step Coverage */}
                  <div>
                    <FieldLabel tooltip="Prozentsatz der Opportunities, bei denen klar dokumentiert ist, was der nächste konkrete Schritt ist.">Bei wie viel % der Opportunities ist ein Next Step definiert?</FieldLabel>
                    <NumberInput
                      value={form.nextStepCoveragePercent}
                      onChange={(v) => { update("nextStepCoveragePercent", v); if (v !== null) update("nextStepCoveragePercent_unknown", false); }}
                      placeholder="z. B. 60"
                      min={0}
                      max={100}
                      suffix="%"
                      unknownId="nextStepCoveragePercent_unknown"
                      unknownChecked={!!form.nextStepCoveragePercent_unknown}
                      onUnknownChange={(checked) => { update("nextStepCoveragePercent_unknown", checked); if (checked) update("nextStepCoveragePercent", null); }}
                    />
                  </div>

                  {/* Forecast Accuracy */}
                  <div>
                    <FieldLabel tooltip="Durchschnittliche Abweichung zwischen prognostiziertem und tatsächlichem Umsatz.">Wie genau ist Ihr Forecast typischerweise?</FieldLabel>
                    <SelectInput
                      value={form.forecastAccuracy}
                      onChange={(v) => { update("forecastAccuracy", v); update("forecastAccuracy_unknown", false); }}
                      options={FORECAST_ACCURACY_OPTIONS}
                      unknownId="forecastAccuracy_unknown"
                      unknownChecked={!!form.forecastAccuracy_unknown}
                      onUnknownChange={(checked) => { update("forecastAccuracy_unknown", checked); }}
                    />
                  </div>

                  {/* Pipeline Review Cadence */}
                  <div>
                    <FieldLabel tooltip="Regelmäßigkeit der strukturierten Pipeline-Reviews mit dem Team.">Wie oft wird die Pipeline systematisch reviewed?</FieldLabel>
                    <SelectInput
                      value={form.pipelineReviewCadence}
                      onChange={(v) => { update("pipelineReviewCadence", v); update("pipelineReviewCadence_unknown", false); }}
                      options={REVIEW_CADENCE_OPTIONS}
                      unknownId="pipelineReviewCadence_unknown"
                      unknownChecked={!!form.pipelineReviewCadence_unknown}
                      onUnknownChange={(checked) => { update("pipelineReviewCadence_unknown", checked); }}
                    />
                  </div>

                  {/* CRM Usage */}
                  <div>
                    <FieldLabel tooltip="Grad der Nutzung und Pflege Ihres CRM-Systems durch das Sales-Team.">Wie konsequent wird Ihr CRM genutzt?</FieldLabel>
                    <SelectInput
                      value={form.crmUsage}
                      onChange={(v) => { update("crmUsage", v); update("crmUsage_unknown", false); }}
                      options={CRM_USAGE_OPTIONS}
                      unknownId="crmUsage_unknown"
                      unknownChecked={!!form.crmUsage_unknown}
                      onUnknownChange={(checked) => { update("crmUsage_unknown", checked); }}
                    />
                  </div>

                  {/* Stale Pipeline Share (NEW) */}
                  <div>
                    <FieldLabel tooltip="Anteil der Opportunities in der Pipeline, die seit mehr als 30 Tagen keine Aktivität verzeichnet haben.">Wie viel % Ihrer Pipeline-Opportunities haben seit über 30 Tagen keine Aktivität?</FieldLabel>
                    <NumberInput
                      value={form.stalePipelineSharePercent}
                      onChange={(v) => { update("stalePipelineSharePercent", v); if (v !== null) update("stalePipelineSharePercent_unknown", false); }}
                      placeholder="z. B. 25"
                      suffix="%"
                      min={0}
                      max={100}
                      unknownId="stalePipelineSharePercent_unknown"
                      unknownChecked={!!form.stalePipelineSharePercent_unknown}
                      onUnknownChange={(checked) => { update("stalePipelineSharePercent_unknown", checked); if (checked) update("stalePipelineSharePercent", null); }}
                    />
                    <FieldHint>Optional – über 30% deutet auf Pipeline-Inflation und schlechte Hygiene hin.</FieldHint>
                  </div>

                  {/* Forecast Coverage Ratio (NEW) */}
                  <div>
                    <FieldLabel tooltip="Verhältnis von gesamter Pipeline zu monatlichem Umsatzziel. Ein gesundes Verhältnis liegt typischerweise bei 3x–4x.">Wie hoch ist Ihre Pipeline im Verhältnis zum monatlichen Umsatzziel? (Faktor)</FieldLabel>
                    <NumberInput
                      value={form.forecastCoverageRatio}
                      onChange={(v) => { update("forecastCoverageRatio", v); if (v !== null) update("forecastCoverageRatio_unknown", false); }}
                      placeholder="z. B. 3.0"
                      min={0}
                      unknownId="forecastCoverageRatio_unknown"
                      unknownChecked={!!form.forecastCoverageRatio_unknown}
                      onUnknownChange={(checked) => { update("forecastCoverageRatio_unknown", checked); if (checked) update("forecastCoverageRatio", null); }}
                    />
                    <FieldHint>Optional – z. B. 3,0 bedeutet, Ihre Pipeline entspricht dem 3-fachen des Monatsziels.</FieldHint>
                  </div>
                </div>
              </SectionBox>
            )}

            {/* STEP 4: SALES REP PERFORMANCE */}
            {currentStep === 5 && (
              <SectionBox
                title="Sales Rep Performance"
                subtitle="Zielerreichung, Pace, Teamunterschiede und Pipeline-Realität"
              >
                <div className="space-y-8">
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <FieldLabel tooltip="Anzahl der aktuell voll produktiven Sales Reps.">
                        Wie viele produktive Sales Reps haben Sie aktuell?
                      </FieldLabel>
                      <NumberInput
                        value={form.productiveReps}
                        onChange={(v) => { update("productiveReps", v); if (v !== null) update("productiveReps_unknown", false); }}
                        min={0}
                        placeholder="z. B. 5"
                        unknownId="productiveReps_unknown"
                        unknownChecked={!!form.productiveReps_unknown}
                        onUnknownChange={(checked) => { update("productiveReps_unknown", checked); if (checked) update("productiveReps", null); }}
                      />
                    </div>

                    <div>
                      <FieldLabel tooltip="Wie gut ist die Transparenz über den durchschnittlichen Umsatz pro Rep?">
                        Ist der durchschnittliche Monatsumsatz pro Rep bekannt?
                      </FieldLabel>
                      <SelectInput
                        value={form.avgRevenuePerRepVisibility}
                        onChange={(v) => {
                          update("avgRevenuePerRepVisibility", v);
                          update("avgRevenuePerRepVisibility_unknown", false);
                        }}
                        options={REVENUE_VISIBILITY_OPTIONS}
                        unknownId="avgRevenuePerRepVisibility_unknown"
                        unknownChecked={!!form.avgRevenuePerRepVisibility_unknown}
                        onUnknownChange={(checked) => { update("avgRevenuePerRepVisibility_unknown", checked); }}
                      />
                    </div>
                  </div>

                  {!form.avgRevenuePerRepVisibility_unknown && (
                    <div className="grid gap-6 md:grid-cols-1">
                      <div>
                        <FieldLabel tooltip="Durchschnittlicher Monatsumsatz eines produktiven Reps.">
                          Durchschnittlicher Monatsumsatz pro Rep (€)
                        </FieldLabel>
                        <NumberInput
                          value={form.avgRevenuePerRep}
                          onChange={(v) => { update("avgRevenuePerRep", v); if (v !== null) update("avgRevenuePerRep_unknown", false); }}
                          min={0}
                          placeholder="z. B. 25000"
                          unknownId="avgRevenuePerRep_unknown"
                          unknownChecked={!!form.avgRevenuePerRep_unknown}
                          onUnknownChange={(checked) => { update("avgRevenuePerRep_unknown", checked); if (checked) update("avgRevenuePerRep", null); }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <FieldLabel tooltip="Geplantes Sales-Umsatzziel für das laufende Jahr.">
                        Was ist Ihr Umsatzziel für das laufende Jahr? (€)
                      </FieldLabel>
                      <NumberInput
                        value={form.totalRevenueTargetYear}
                        onChange={(v) => { update("totalRevenueTargetYear", v); if (v !== null) update("totalRevenueTargetYear_unknown", false); }}
                        min={0}
                        placeholder="z. B. 1000000"
                        unknownId="totalRevenueTargetYear_unknown"
                        unknownChecked={!!form.totalRevenueTargetYear_unknown}
                        onUnknownChange={(checked) => { update("totalRevenueTargetYear_unknown", checked); if (checked) update("totalRevenueTargetYear", null); }}
                      />
                    </div>

                    <div>
                      <FieldLabel tooltip="Bisher erzielter Sales-Umsatz im laufenden Jahr.">
                        Wie viel Umsatz haben Sie in diesem Jahr bisher erzielt? (€)
                      </FieldLabel>
                      <NumberInput
                        value={form.revenueAchievedYTD}
                        onChange={(v) => { update("revenueAchievedYTD", v); if (v !== null) update("revenueAchievedYTD_unknown", false); }}
                        min={0}
                        placeholder="z. B. 250000"
                        unknownId="revenueAchievedYTD_unknown"
                        unknownChecked={!!form.revenueAchievedYTD_unknown}
                        onUnknownChange={(checked) => { update("revenueAchievedYTD_unknown", checked); if (checked) update("revenueAchievedYTD", null); }}
                      />
                    </div>
                  </div>

                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <FieldLabel tooltip="Ist das Ziel operativ auf Reps oder Teams heruntergebrochen?">
                        Ist das Umsatzziel klar auf Reps oder Teams heruntergebrochen?
                      </FieldLabel>
                      <SelectInput
                        value={form.revenueTargetBrokenDownToReps}
                        onChange={(v) => { update("revenueTargetBrokenDownToReps", v); update("revenueTargetBrokenDownToReps_unknown", false); }}
                        options={REVENUE_TARGET_BREAKDOWN_OPTIONS}
                        unknownId="revenueTargetBrokenDownToReps_unknown"
                        unknownChecked={!!form.revenueTargetBrokenDownToReps_unknown}
                        onUnknownChange={(checked) => { update("revenueTargetBrokenDownToReps_unknown", checked); }}
                      />
                    </div>

                    <div>
                      <FieldLabel tooltip="Gibt es zusätzlich ein monatliches Umsatzziel pro Rep?">
                        Gibt es ein monatliches Umsatzziel pro Rep?
                      </FieldLabel>
                      <SelectInput
                        value={form.repsHaveRevenueTarget}
                        onChange={(v) => { update("repsHaveRevenueTarget", v); update("repsHaveRevenueTarget_unknown", false); }}
                        options={YES_NO_UNKNOWN_OPTIONS}
                        unknownId="repsHaveRevenueTarget_unknown"
                        unknownChecked={!!form.repsHaveRevenueTarget_unknown}
                        onUnknownChange={(checked) => { update("repsHaveRevenueTarget_unknown", checked); }}
                      />
                    </div>
                  </div>

                  {form.repsHaveRevenueTarget === "yes" && (
                    <div className="grid gap-6 md:grid-cols-1">
                      <div>
                        <FieldLabel tooltip="Monatliches Ziel pro Rep. Damit kann die Teamziel-Logik validiert werden.">
                          Wie hoch ist das monatliche Umsatzziel pro Rep? (€)
                        </FieldLabel>
                        <NumberInput
                          value={form.avgRevenueTargetPerRep}
                          onChange={(v) => { update("avgRevenueTargetPerRep", v); if (v !== null) update("avgRevenueTargetPerRep_unknown", false); }}
                          min={0}
                          placeholder="z. B. 15000"
                          unknownId="avgRevenueTargetPerRep_unknown"
                          unknownChecked={!!form.avgRevenueTargetPerRep_unknown}
                          onUnknownChange={(checked) => { update("avgRevenueTargetPerRep_unknown", checked); if (checked) update("avgRevenueTargetPerRep", null); }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <FieldLabel tooltip="Wie viele Reps liegen aktuell im Plan zur Zielerreichung?">
                        Wie viele Ihrer Sales Reps liegen aktuell im Plan zur Zielerreichung?
                      </FieldLabel>
                      <SelectInput
                        value={form.repsOnTrackToTarget}
                        onChange={(v) => { update("repsOnTrackToTarget", v); update("repsOnTrackToTarget_unknown", false); }}
                        options={REPS_ON_TRACK_OPTIONS}
                        unknownId="repsOnTrackToTarget_unknown"
                        unknownChecked={!!form.repsOnTrackToTarget_unknown}
                        onUnknownChange={(checked) => { update("repsOnTrackToTarget_unknown", checked); }}
                      />
                    </div>

                    <div>
                      <FieldLabel tooltip="Wie stark unterscheiden sich Umsatz, Aktivität oder Abschlusskraft im Team?">
                        Wie stark unterscheidet sich die Leistung Ihrer Sales Reps?
                      </FieldLabel>
                      <SelectInput
                        value={form.repPerformanceVariance}
                        onChange={(v) => { update("repPerformanceVariance", v); update("repPerformanceVariance_unknown", false); }}
                        options={PERFORMANCE_VARIANCE_OPTIONS}
                        unknownId="repPerformanceVariance_unknown"
                        unknownChecked={!!form.repPerformanceVariance_unknown}
                        onUnknownChange={(checked) => { update("repPerformanceVariance_unknown", checked); }}
                      />
                    </div>
                  </div>

                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <FieldLabel tooltip="Wie viele Reps haben aktuell genug Pipeline, um ihr Ziel zu erreichen?">
                        Wie viele Ihrer Reps haben aktuell genug Pipeline, um ihr Ziel zu erreichen?
                      </FieldLabel>
                      <SelectInput
                        value={form.repsWithEnoughPipeline}
                        onChange={(v) => { update("repsWithEnoughPipeline", v); update("repsWithEnoughPipeline_unknown", false); }}
                        options={REPS_WITH_ENOUGH_PIPELINE_OPTIONS}
                        unknownId="repsWithEnoughPipeline_unknown"
                        unknownChecked={!!form.repsWithEnoughPipeline_unknown}
                        onUnknownChange={(checked) => { update("repsWithEnoughPipeline_unknown", checked); }}
                      />
                    </div>

                    <div>
                      <FieldLabel tooltip="Wie sicher sind Sie, dass Ihre Pipeline tatsächlich zu Abschlüssen führt?">
                        Wie sicher sind Sie, dass Ihre Pipeline zu Abschlüssen führt?
                      </FieldLabel>
                      <SelectInput
                        value={form.pipelineConfidence}
                        onChange={(v) => { update("pipelineConfidence", v); update("pipelineConfidence_unknown", false); }}
                        options={PIPELINE_CONFIDENCE_OPTIONS}
                        unknownId="pipelineConfidence_unknown"
                        unknownChecked={!!form.pipelineConfidence_unknown}
                        onUnknownChange={(checked) => { update("pipelineConfidence_unknown", checked); }}
                      />
                    </div>
                  </div>

                  <div className="grid gap-6 md:grid-cols-1">
                    <div>
                      <FieldLabel tooltip="Wie regelmäßig wird die Leistung einzelner Reps systematisch reviewed?">
                        Wie regelmäßig wird die Performance einzelner Reps systematisch reviewed?
                      </FieldLabel>
                      <SelectInput
                        value={form.repReviewCadence}
                        onChange={(v) => { update("repReviewCadence", v); update("repReviewCadence_unknown", false); }}
                        options={REP_REVIEW_CADENCE_OPTIONS}
                        unknownId="repReviewCadence_unknown"
                        unknownChecked={!!form.repReviewCadence_unknown}
                        onUnknownChange={(checked) => { update("repReviewCadence_unknown", checked); }}
                      />
                    </div>
                  </div>

                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                    <strong>Warum wir das fragen:</strong> Dieser Block zeigt, ob Ihr Team auf Kurs ist,
                    wie groß die Ziel-Lücke zum heutigen Zeitpunkt ausfällt, wie stark die Leistung im Team streut
                    und ob genug belastbare Pipeline vorhanden ist, um das Ziel realistisch zu erreichen.
                  </div>
                </div>
              </SectionBox>
            )}

            {currentStep === 4 && (
              <SectionBox
                title="Lead Quality & Source"
                subtitle="Nicht alle Leads sind gleich wertvoll"
              >
                <div className="space-y-6">
                  {/* Pipeline Source */}
                  <div>
                    <FieldLabel tooltip="Primäre Quelle Ihrer Sales-Opportunities (z.B. Inbound, Outbound, Partner).">Woher kommt Ihre Pipeline hauptsächlich?</FieldLabel>
                    <SelectInput
                      value={form.pipelineSource}
                      onChange={(v) => { update("pipelineSource", v); update("pipelineSource_unknown", false); }}
                      options={SOURCE_OPTIONS}
                      unknownId="pipelineSource_unknown"
                      unknownChecked={!!form.pipelineSource_unknown}
                      onUnknownChange={(checked) => { update("pipelineSource_unknown", checked); }}
                    />
                  </div>

                  {/* Lead Quality */}
                  <div>
                    <FieldLabel tooltip="Einschätzung, wie gut die eingehenden Leads zu Ihrem Ideal Customer Profile passen.">Wie bewerten Sie die Qualität Ihrer eingehenden Leads?</FieldLabel>
                    <SelectInput
                      value={form.leadQuality}
                      onChange={(v) => { update("leadQuality", v); update("leadQuality_unknown", false); }}
                      options={LEAD_QUALITY_OPTIONS}
                      unknownId="leadQuality_unknown"
                      unknownChecked={!!form.leadQuality_unknown}
                      onUnknownChange={(checked) => { update("leadQuality_unknown", checked); }}
                    />
                  </div>

                  {/* ICP Clarity */}
                  <div>
                    <FieldLabel tooltip="Wie klar und dokumentiert ist Ihr Ideal Customer Profile (ICP)? Ein klarer ICP reduziert Streuverluste und erhöht die Qualifizierungsrate.">Wie klar ist Ihr Ideal Customer Profile (ICP) definiert?</FieldLabel>
                    <SelectInput
                      value={form.icpClarity}
                      onChange={(v) => {
                        update("icpClarity", v);
                        update("icpClarity_unknown", false);
                        // Keep icpDefined in sync for backward compat
                        if (v === "very-clear" || v === "mostly-clear") update("icpDefined", "yes");
                        else if (v === "vague") update("icpDefined", "partly");
                        else if (v === "not-defined") update("icpDefined", "no");
                        else update("icpDefined", "not-known");
                      }}
                      options={ICP_CLARITY_OPTIONS}
                      unknownId="icpClarity_unknown"
                      unknownChecked={!!form.icpClarity_unknown}
                      onUnknownChange={(checked) => { update("icpClarity_unknown", checked); }}
                    />
                    <FieldHint>Ein klar dokumentierter ICP ist die Grundlage für effektive Lead-Qualifizierung und gezielten Akquise-Mix.</FieldHint>
                  </div>

                  {/* Lead Scoring Maturity (NEW) */}
                  <div>
                    <FieldLabel tooltip="Gibt es ein formales Lead-Scoring-System, oder wird die Priorisierung intuitiv vorgenommen?">Wie reif ist Ihr Lead Scoring?</FieldLabel>
                    <SelectInput
                      value={form.leadScoringMaturity}
                      onChange={(v) => { update("leadScoringMaturity", v); update("leadScoringMaturity_unknown", false); }}
                      options={LEAD_SCORING_OPTIONS}
                      unknownId="leadScoringMaturity_unknown"
                      unknownChecked={!!form.leadScoringMaturity_unknown}
                      onUnknownChange={(checked) => { update("leadScoringMaturity_unknown", checked); }}
                    />
                  </div>

                  {/* Qualified Lead Rate % (NEW) */}
                  <div>
                    <FieldLabel tooltip="Anteil der eingehenden Leads, die nach der Qualifizierung als wirklich geeignet eingestuft werden.">Wie viel % der eingehenden Leads qualifizieren sich nach dem Erstgespräch als geeignet?</FieldLabel>
                    <NumberInput
                      value={form.qualifiedLeadRatePercent}
                      onChange={(v) => { update("qualifiedLeadRatePercent", v); if (v !== null) update("qualifiedLeadRatePercent_unknown", false); }}
                      placeholder="z. B. 35"
                      suffix="%"
                      min={0}
                      max={100}
                      unknownId="qualifiedLeadRatePercent_unknown"
                      unknownChecked={!!form.qualifiedLeadRatePercent_unknown}
                      onUnknownChange={(checked) => { update("qualifiedLeadRatePercent_unknown", checked); if (checked) update("qualifiedLeadRatePercent", null); }}
                    />
                    <FieldHint>Optional – ein Wert unter 30% kann auf ICP-Probleme oder schlechte Lead-Quellen hinweisen.</FieldHint>
                  </div>

                  {/* Market Presence */}
                  <div>
                    <FieldLabel tooltip="Wie bekannt und sichtbar sind Sie in Ihrem Zielmarkt? Starke Marktpräsenz verbessert Inbound-Qualität und Closing-Raten.">Wie stark ist Ihre Marktpräsenz in Ihrem Zielsegment?</FieldLabel>
                    <SelectInput
                      value={form.marketPresence}
                      onChange={(v) => { update("marketPresence", v); update("marketPresence_unknown", false); }}
                      options={MARKET_PRESENCE_OPTIONS}
                      unknownId="marketPresence_unknown"
                      unknownChecked={!!form.marketPresence_unknown}
                      onUnknownChange={(checked) => { update("marketPresence_unknown", checked); }}
                    />
                    <FieldHint>Marktpräsenz beeinflusst, wie leicht Leads konvertieren und wie viel Überzeugungsarbeit nötig ist.</FieldHint>
                  </div>
                </div>
              </SectionBox>
            )}

            {/* STEP 5b: AKQUISE-MIX */}
            {currentStep === 6 && (
              <SectionBox
                title="Akquise-Mix"
                subtitle="Wie verteilt sich Ihre Neukundengewinnung auf verschiedene Kanäle? (Summe muss 100% ergeben)"
              >
                <div className="space-y-6">
                  {/* Acquisition Channels */}
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <FieldLabel tooltip="Anteil der Neukundengewinnung über Telefon (Cold Calling, Warm Calling)">Telefon</FieldLabel>
                      <NumberInput
                        value={form.acquisitionPhone}
                        onChange={(v) => update("acquisitionPhone", v)}
                        placeholder="0"
                        min={0}
                        max={100}
                        suffix="%"
                      />
                    </div>
                    <div>
                      <FieldLabel tooltip="Anteil der Neukundengewinnung über E-Mail (Cold Mails, Kampagnen)">E-Mail</FieldLabel>
                      <NumberInput
                        value={form.acquisitionEmail}
                        onChange={(v) => update("acquisitionEmail", v)}
                        placeholder="0"
                        min={0}
                        max={100}
                        suffix="%"
                      />
                    </div>
                    <div>
                      <FieldLabel tooltip="Anteil der Neukundengewinnung über LinkedIn (Social Selling, InMails)">LinkedIn</FieldLabel>
                      <NumberInput
                        value={form.acquisitionLinkedIn}
                        onChange={(v) => update("acquisitionLinkedIn", v)}
                        placeholder="0"
                        min={0}
                        max={100}
                        suffix="%"
                      />
                    </div>
                    <div>
                      <FieldLabel tooltip="Anteil der Neukundengewinnung über Xing">Xing</FieldLabel>
                      <NumberInput
                        value={form.acquisitionXing}
                        onChange={(v) => update("acquisitionXing", v)}
                        placeholder="0"
                        min={0}
                        max={100}
                        suffix="%"
                      />
                    </div>
                    <div>
                      <FieldLabel tooltip="Anteil der Neukundengewinnung über Empfehlungen und Referrals">Empfehlungen</FieldLabel>
                      <NumberInput
                        value={form.acquisitionReferrals}
                        onChange={(v) => update("acquisitionReferrals", v)}
                        placeholder="0"
                        min={0}
                        max={100}
                        suffix="%"
                      />
                    </div>
                    <div>
                      <FieldLabel tooltip="Anteil der Neukundengewinnung über Events, Messen und Konferenzen">Events & Messen</FieldLabel>
                      <NumberInput
                        value={form.acquisitionEvents}
                        onChange={(v) => update("acquisitionEvents", v)}
                        placeholder="0"
                        min={0}
                        max={100}
                        suffix="%"
                      />
                    </div>
                    <div>
                      <FieldLabel tooltip="Anteil der Neukundengewinnung über Content Marketing (Blog, Webinare, etc.)">Content Marketing</FieldLabel>
                      <NumberInput
                        value={form.acquisitionContentMarketing}
                        onChange={(v) => update("acquisitionContentMarketing", v)}
                        placeholder="0"
                        min={0}
                        max={100}
                        suffix="%"
                      />
                    </div>
                    <div>
                      <FieldLabel tooltip="Anteil der Neukundengewinnung über bezahlte Werbung (Google Ads, LinkedIn Ads, etc.)">Paid Advertising</FieldLabel>
                      <NumberInput
                        value={form.acquisitionPaidAds}
                        onChange={(v) => update("acquisitionPaidAds", v)}
                        placeholder="0"
                        min={0}
                        max={100}
                        suffix="%"
                      />
                    </div>
                    <div>
                      <FieldLabel tooltip="Anteil der Neukundengewinnung über persönliche Besuche (Cold Visits)">Cold Visits</FieldLabel>
                      <NumberInput
                        value={form.acquisitionColdVisits}
                        onChange={(v) => update("acquisitionColdVisits", v)}
                        placeholder="0"
                        min={0}
                        max={100}
                        suffix="%"
                      />
                    </div>
                    <div>
                      <FieldLabel tooltip="Anteil der Neukundengewinnung über Partnerschaften und Kooperationen">Partnerschaften</FieldLabel>
                      <NumberInput
                        value={form.acquisitionPartner}
                        onChange={(v) => update("acquisitionPartner", v)}
                        placeholder="0"
                        min={0}
                        max={100}
                        suffix="%"
                      />
                    </div>
                    <div>
                      <FieldLabel tooltip="Anteil der Neukundengewinnung über sonstige Kanäle">Sonstige</FieldLabel>
                      <NumberInput
                        value={form.acquisitionOther}
                        onChange={(v) => update("acquisitionOther", v)}
                        placeholder="0"
                        min={0}
                        max={100}
                        suffix="%"
                      />
                    </div>
                  </div>

                  {/* Total Display */}
                  <div className="rounded-xl border-2 border-primary/20 bg-primary/5 p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-foreground">Summe:</span>
                      <span className={`text-2xl font-bold ${
                        ((form.acquisitionPhone ?? 0) +
                        (form.acquisitionEmail ?? 0) +
                        (form.acquisitionLinkedIn ?? 0) +
                        (form.acquisitionXing ?? 0) +
                        (form.acquisitionReferrals ?? 0) +
                        (form.acquisitionEvents ?? 0) +
                        (form.acquisitionContentMarketing ?? 0) +
                        (form.acquisitionPaidAds ?? 0) +
                        (form.acquisitionColdVisits ?? 0) +
                        (form.acquisitionPartner ?? 0) +
                        (form.acquisitionOther ?? 0)) === 100
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}>
                        {(form.acquisitionPhone ?? 0) +
                        (form.acquisitionEmail ?? 0) +
                        (form.acquisitionLinkedIn ?? 0) +
                        (form.acquisitionXing ?? 0) +
                        (form.acquisitionReferrals ?? 0) +
                        (form.acquisitionEvents ?? 0) +
                        (form.acquisitionContentMarketing ?? 0) +
                        (form.acquisitionPaidAds ?? 0) +
                        (form.acquisitionColdVisits ?? 0) +
                        (form.acquisitionPartner ?? 0) +
                        (form.acquisitionOther ?? 0)}%
                      </span>
                    </div>
                  </div>
                </div>
              </SectionBox>
            )}

            {/* STEP 6: TOOLS & SYSTEMS */}
            {currentStep === 7 && (
              <SectionBox
                title="Tools & Systems"
                subtitle="Welche Systeme tragen oder bremsen Ihren Prozess?"
              >
                <div className="space-y-6">
                  {/* CRM Available */}
                  <div>
                    <FieldLabel tooltip="Verwenden Sie ein Customer Relationship Management System zur Verwaltung Ihrer Kundenbeziehungen?">Nutzen Sie ein CRM-System?</FieldLabel>
                    <SelectInput
                      value={form.crmAvailable}
                      onChange={(v) => { update("crmAvailable", v); update("crmAvailable_unknown", false); }}
                      options={YES_NO_UNKNOWN_OPTIONS}
                      unknownId="crmAvailable_unknown"
                      unknownChecked={!!form.crmAvailable_unknown}
                      onUnknownChange={(checked) => { update("crmAvailable_unknown", checked); }}
                    />
                  </div>

                  {/* CRM Name */}
                  {(form.crmAvailable === "yes" || form.crmAvailable === "partly") && (
                    <div>
                      <FieldLabel tooltip="Name Ihres CRM-Systems (z.B. HubSpot, Salesforce, Pipedrive).">Welches CRM nutzen Sie?</FieldLabel>
                      <TextInput
                        value={form.crmName}
                        onChange={(v) => update("crmName", v)}
                        placeholder="z. B. HubSpot, Salesforce, Pipedrive"
                      />
                    </div>
                  )}

                  {/* Marketing Automation */}
                  <div>
                    <FieldLabel tooltip="Setzen Sie Tools zur Automatisierung von Marketing-Prozessen ein (z.B. E-Mail-Kampagnen, Lead Nurturing)?">Nutzen Sie Marketing Automation?</FieldLabel>
                    <SelectInput
                      value={form.marketingAutomation}
                      onChange={(v) => { update("marketingAutomation", v); update("marketingAutomation_unknown", false); }}
                      options={YES_NO_UNKNOWN_OPTIONS}
                      unknownId="marketingAutomation_unknown"
                      unknownChecked={!!form.marketingAutomation_unknown}
                      onUnknownChange={(checked) => { update("marketingAutomation_unknown", checked); }}
                    />
                  </div>

                  {/* Lead Routing */}
                  <div>
                    <FieldLabel tooltip="Werden eingehende Leads automatisch an die richtigen Vertriebsmitarbeiter verteilt?">Gibt es automatisches Lead Routing?</FieldLabel>
                    <SelectInput
                      value={form.leadRouting}
                      onChange={(v) => { update("leadRouting", v); update("leadRouting_unknown", false); }}
                      options={YES_NO_UNKNOWN_OPTIONS}
                      unknownId="leadRouting_unknown"
                      unknownChecked={!!form.leadRouting_unknown}
                      onUnknownChange={(checked) => { update("leadRouting_unknown", checked); }}
                    />
                  </div>

                  {/* Sales Playbook / Prozessdokumentation */}
                  <div>
                    <FieldLabel tooltip="Gibt es ein dokumentiertes Sales Playbook oder einen definierten Vertriebsprozess, der aktiv genutzt wird?">Gibt es ein dokumentiertes Sales Playbook oder einen definierten Vertriebsprozess?</FieldLabel>
                    <SelectInput
                      value={form.salesPlaybookAvailable}
                      onChange={(v) => { update("salesPlaybookAvailable", v); update("salesPlaybookAvailable_unknown", false); }}
                      options={YES_NO_UNKNOWN_OPTIONS}
                      unknownId="salesPlaybookAvailable_unknown"
                      unknownChecked={!!form.salesPlaybookAvailable_unknown}
                      onUnknownChange={(checked) => { update("salesPlaybookAvailable_unknown", checked); }}
                    />
                  </div>
                </div>
              </SectionBox>
            )}

            {/* STEP 7: GOAL & PAIN */}
            {currentStep === 8 && (
              <SectionBox
                title="Ziel & Engpass"
                subtitle="Was Sie erreichen wollen und was Sie aktuell blockiert"
              >
                <div className="space-y-6">
                  {/* Sales Goal */}
                  <div>
                    <FieldLabel tooltip="Wählen Sie das primäre Ziel, das Sie mit Ihrem Vertrieb erreichen möchten.">Was ist Ihr wichtigstes Vertriebsziel?</FieldLabel>
                    <div className="grid grid-cols-1 gap-3">
                      {GOAL_OPTIONS.map((option) => (
                        <OptionCard
                          key={option.value}
                          value={option.value}
                          current={form.salesGoal}
                          label={option.label}
                          onSelect={(v) => update("salesGoal", v)}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Primary Pain */}
                  <div>
                    <FieldLabel tooltip="Identifizieren Sie die größte Herausforderung, die Ihr Vertriebswachstum aktuell bremst.">Was ist aktuell Ihr größter Engpass?</FieldLabel>
                    <div className="grid grid-cols-1 gap-3">
                      {PAIN_OPTIONS.map((option) => (
                        <OptionCard
                          key={option.value}
                          value={option.value}
                          current={form.primaryPain}
                          label={option.label}
                          onSelect={(v) => update("primaryPain", v)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </SectionBox>
            )}

            {/* STEP 8: COMPANY CONTEXT */}
            {currentStep === 9 && (
              <SectionBox
                title="Unternehmenskontext"
                subtitle="Für Benchmarking und kontextuelle Einordnung"
              >
                <div className="space-y-6">
                  {/* Company Name */}
                  <div>
                    <FieldLabel required tooltip="Name Ihres Unternehmens für die personalisierte Auswertung.">Unternehmensname</FieldLabel>
                    <TextInput
                      value={form.companyName}
                      onChange={(v) => update("companyName", v)}
                      placeholder="Ihr Unternehmensname"
                    />
                  </div>

                  {/* Company Domain */}
                  <div>
                    <FieldLabel tooltip="Website-Domain Ihres Unternehmens (z.B. beispiel.de). Wird für KI-gestützte Analyse verwendet.">Website-Domain (optional)</FieldLabel>
                    <TextInput
                      value={form.companyDomain}
                      onChange={(v) => update("companyDomain", v)}
                      placeholder="beispiel.de"
                    />
                  </div>

                  {/* Industry */}
                  <div>
                    <FieldLabel tooltip="Ihre Branche oder Industrie für branchenspezifische Benchmarks.">Branche</FieldLabel>
                    <TextInput
                      value={form.industry}
                      onChange={(v) => update("industry", v)}
                      placeholder="z. B. SaaS, Consulting, Industrie"
                    />
                  </div>

                  {/* Company Size */}
                  <div>
                    <FieldLabel tooltip="Anzahl der Mitarbeiter in Ihrem Unternehmen.">Unternehmensgröße</FieldLabel>
                    <SelectInput
                      value={form.companySize}
                      onChange={(v) => { update("companySize", v); update("companySize_unknown", false); }}
                      options={COMPANY_SIZE_OPTIONS}
                      unknownId="companySize_unknown"
                      unknownChecked={!!form.companySize_unknown}
                      onUnknownChange={(checked) => { update("companySize_unknown", checked); }}
                    />
                  </div>

                  {/* Team Size wird automatisch aus Step 5 (productiveReps) übernommen */}

                  {/* Business Model */}
                  <div>
                    <FieldLabel tooltip="Ihr primäres Geschäftsmodell (B2B, B2C oder beides).">Geschäftsmodell</FieldLabel>
                    <SelectInput
                      value={form.businessModel}
                      onChange={(v) => { update("businessModel", v); update("businessModel_unknown", false); }}
                      options={BUSINESS_MODEL_OPTIONS}
                      unknownId="businessModel_unknown"
                      unknownChecked={!!form.businessModel_unknown}
                      onUnknownChange={(checked) => { update("businessModel_unknown", checked); }}
                    />
                  </div>
                </div>
              </SectionBox>
            )}

            {/* STEP 9: CONTACT */}
            {currentStep === 10 && (
              <SectionBox
                title="Ansprechpartner"
                subtitle="Für Zustellung und spätere Einordnung"
              >
                {unknownFieldCount >= 5 && (
                  <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                    <span className="text-amber-500 shrink-0 mt-0.5">&#9888;</span>
                    <div>
                      <div className="text-sm font-semibold text-amber-900 mb-0.5">{unknownFieldCount} Felder als „Nicht bekannt“ markiert</div>
                      <p className="text-xs text-amber-800">Das kann die Analysetiefe einschränken. Je mehr reale Zahlen Sie angeben, desto präziser werden Ihre Ergebnisse und Handlungsempfehlungen.</p>
                    </div>
                  </div>
                )}
                <div className="space-y-6">
                  {/* First Name */}
                  <div>
                    <FieldLabel tooltip="Ihr Vorname für die personalisierte Ansprache.">Vorname</FieldLabel>
                    <TextInput
                      value={form.contactFirstName}
                      onChange={(v) => update("contactFirstName", v)}
                      placeholder="Vorname"
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <FieldLabel tooltip="Ihr Nachname für die personalisierte Ansprache.">Nachname</FieldLabel>
                    <TextInput
                      value={form.contactLastName}
                      onChange={(v) => update("contactLastName", v)}
                      placeholder="Nachname"
                    />
                  </div>

                  {/* Role */}
                  <div>
                    <FieldLabel tooltip="Ihre Position im Unternehmen (z.B. Head of Sales, CEO, Vertriebsleiter).">Rolle / Position</FieldLabel>
                    <TextInput
                      value={form.contactRole}
                      onChange={(v) => update("contactRole", v)}
                      placeholder="z. B. Head of Sales, CEO"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <FieldLabel required tooltip="Ihre E-Mail-Adresse für die Zusendung der Audit-Ergebnisse.">E-Mail</FieldLabel>
                    <TextInput
                      type="email"
                      value={form.contactEmail}
                      onChange={(v) => update("contactEmail", v)}
                      placeholder="ihre.email@unternehmen.de"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <FieldLabel tooltip="Ihre Telefonnummer für eventuelle Rückfragen (optional).">Telefon (optional)</FieldLabel>
                    <TextInput
                      type="tel"
                      value={form.contactPhone}
                      onChange={(v) => update("contactPhone", v)}
                      placeholder="+49 ..."
                    />
                  </div>

                  {/* Email Results */}
                  <div className="flex items-center gap-3 p-4 rounded-xl border bg-muted/30">
                    <input
                      type="checkbox"
                      id="wantsEmailResults"
                      checked={form.wantsEmailResults}
                      onChange={(e) => update("wantsEmailResults", e.target.checked)}
                      className="w-5 h-5 rounded border-border"
                    />
                    <label htmlFor="wantsEmailResults" className="text-sm cursor-pointer">
                      Ergebnisse per E-Mail erhalten
                    </label>
                  </div>
                </div>
              </SectionBox>
            )}
          </div>

          {/* FOOTER NAV */}
          <div className="flex items-center justify-between pt-8 border-t">
            <div className="text-sm text-muted-foreground">
              Schritt-Fortschritt: {stepCompletion}% · Gesamt: {overallCompletion}%
            </div>

            <div className="flex gap-3">
              {currentStep > 0 ? (
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-6 py-3 rounded-xl border hover:bg-muted transition-colors flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Zurück
                </button>
              ) : null}
              {currentStep < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-6 py-3 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2 font-semibold"
                >
                  Weiter
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  className="px-8 py-3 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-semibold"
                >
                  {submitLabel}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}